import { compileStyleWithLayerRules } from './map-layer-rules';
import {
  normalizeMapLibreLayoutOverrides,
  normalizeMapLibrePaintOverrides,
} from './map-workbench/styleValueNormalization';
import type {
  LayerRuleBuilderState,
  LayerRuleDefinition,
  LayerRuleLayerState,
  LayerRuleMode,
} from './map-layer-rules';
import { createGroup, ensureLogicRoot, type StyleRuleLogic } from './map-style-workbench/builderLogic';
import type { SemanticThemeColorPalette, SemanticThemeColorToken } from '../theme';

type Expr = any;

type PaintBaseline = {
  defined: Record<string, boolean>;
  values: Record<string, unknown>;
};

type ExperienceBaseline = {
  filter: unknown | null;
  paint: PaintBaseline;
};

type WorkbenchMeta = {
  managed?: boolean;
  source_id?: string;
  source_alias?: string;
  source_layer?: string;
  kind?: string;
  render_stack_entry_id?: string;
  render_stack_role?: string | null;
  interaction_hit_target_px?: number | null;
  experience_baseline?: ExperienceBaseline;
};

type TargetKey = `${string}::${string}`;
const MAP_SOURCE_GEOJSON_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/data\.geojson$/;

export interface MapExperienceViewState {
  center?: [number, number];
  zoom?: number;
  bearing?: number;
  pitch?: number;
}

export interface MapExperienceLegendEntry {
  id: string;
  label: string;
  color?: string | null;
  representation?: MapExperienceStyleRepresentation | null;
  icon_token?: string | null;
}

export interface MapExperienceLegendGroup {
  id: string;
  source_id: string;
  source_layer: string;
  title: string;
  entries: MapExperienceLegendEntry[];
}

export type MapExperienceStyleProfileKind = 'single' | 'categorical' | 'range' | 'rules';
export type MapExperienceStyleRepresentation = 'fill' | 'line' | 'circle' | 'symbol' | 'fill-extrusion';
export type MapExperienceRenderStackRole = 'base' | 'overlay_badge' | 'overlay_label' | 'overlay_symbol';
export type MapExperienceSemanticColorToken = SemanticThemeColorToken;
export type MapExperienceSemanticColorRef = { kind: 'semantic'; token: MapExperienceSemanticColorToken };
export type MapExperienceStyleColorValue = string | MapExperienceSemanticColorRef;
export type MapExperienceThemeColorPalette = SemanticThemeColorPalette;

export interface MapExperienceStyleInteractionConfig {
  hit_target_px?: number | null;
}

interface MapExperienceStyleProfileBase {
  id: string;
  source_id: string;
  source_layer: string;
  kind: MapExperienceStyleProfileKind;
  representation?: MapExperienceStyleRepresentation | null;
  label?: string | null;
  interaction?: MapExperienceStyleInteractionConfig | null;
}

type MapExperienceStyleOverrideRecord = Record<string, unknown>;

export interface MapExperienceSingleStyleProfile extends MapExperienceStyleProfileBase {
  kind: 'single';
  color: MapExperienceStyleColorValue;
  legend_label?: string | null;
  icon_image?: string | null;
  paint?: MapExperienceStyleOverrideRecord | null;
  layout?: MapExperienceStyleOverrideRecord | null;
}

export interface MapExperienceCategoricalStyleProfile extends MapExperienceStyleProfileBase {
  kind: 'categorical';
  field: string;
  categories: Array<{
    value: string;
    label: string;
    color: MapExperienceStyleColorValue;
    hidden?: boolean;
    icon_image?: string | null;
    paint?: MapExperienceStyleOverrideRecord | null;
    layout?: MapExperienceStyleOverrideRecord | null;
  }>;
  fallback_color?: MapExperienceStyleColorValue | null;
}

export interface MapExperienceRangeBreak {
  id?: string;
  label: string;
  color: MapExperienceStyleColorValue;
  min?: number | null;
  max?: number | null;
  hidden?: boolean;
  icon_image?: string | null;
  paint?: MapExperienceStyleOverrideRecord | null;
  layout?: MapExperienceStyleOverrideRecord | null;
}

export interface MapExperienceRangeStyleProfile extends MapExperienceStyleProfileBase {
  kind: 'range';
  field: string;
  breaks: MapExperienceRangeBreak[];
  fallback_color?: MapExperienceStyleColorValue | null;
}

export interface MapExperienceRulesStyleProfile extends MapExperienceStyleProfileBase {
  kind: 'rules';
  legend_label?: string | null;
  layout?: MapExperienceStyleOverrideRecord | null;
  mode?: LayerRuleMode | null;
  rules?: LayerRuleDefinition[] | null;
}

export type MapExperienceStyleProfile =
  | MapExperienceSingleStyleProfile
  | MapExperienceCategoricalStyleProfile
  | MapExperienceRangeStyleProfile
  | MapExperienceRulesStyleProfile;

export interface MapExperienceRenderStackEntry {
  entry_id: string;
  profile_id: string;
  enabled?: boolean | null;
  label?: string | null;
  stack_role?: MapExperienceRenderStackRole | null;
  filter_logic?: StyleRuleLogic | null;
}

export interface MapExperienceRenderStackDefinition {
  source_id: string;
  source_layer: string;
  entries: MapExperienceRenderStackEntry[];
}

export type MapExperienceFilterKind =
  | 'boolean'
  | 'single_select'
  | 'multi_select'
  | 'numeric_range'
  | 'date_range'
  | 'text_contains';

export type MapExperienceFilterViewerPresentation =
  | 'range'
  | 'max_slider';

export type MapExperienceFilterOperator =
  | 'equals'
  | 'not_equals'
  | 'exists'
  | 'not_exists'
  | 'greater_than'
  | 'less_than';
export type MapExperienceServerExecution = 'client_only' | 'auto' | 'server_only';

export interface MapExperienceFilterOption {
  value: string;
  label?: string | null;
}

export interface MapExperienceFilterDefinition {
  id: string;
  kind: MapExperienceFilterKind;
  field: string;
  operator?: MapExperienceFilterOperator | null;
  value?: string | number | boolean | null;
  label?: string | null;
  description?: string | null;
  editable_in_view?: boolean | null;
  options?: MapExperienceFilterOption[] | null;
  placeholder?: string | null;
  viewer_presentation?: MapExperienceFilterViewerPresentation | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  default_value?: unknown;
  server_binding_key?: string | null;
  server_execution?: MapExperienceServerExecution | null;
}

export interface MapExperienceFilterSet {
  id: string;
  source_id: string;
  source_layer: string;
  label?: string | null;
  filters: MapExperienceFilterDefinition[];
}

export interface MapExperiencePresetDefinition {
  id: string;
  label: string;
  viewport?: MapExperienceViewState | null;
  layer_visibility?: Record<string, boolean> | null;
  active_style_profiles?: Record<string, string>;
  active_filter_values?: Record<string, Record<string, unknown>>;
}

export type MapExperienceInteractivityGeometryKind = 'point' | 'line' | 'polygon' | 'mixed' | 'unknown';
export type MapExperienceInteractivityValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'enum'
  | 'geometry'
  | 'feature_ref'
  | 'feature_ref_list';
export type MapExperienceInteractivityFieldSourceKind =
  | 'feature_property'
  | 'enrichment'
  | 'resolver_output'
  | 'builtin';
export type MapExperienceInteractivitySpatialQueryMode = 'none' | 'rendered_only' | 'visible_only' | 'dataset_wide';
export type MapExperienceBuiltinValueId =
  | 'my_location'
  | 'selected_feature'
  | 'hovered_feature'
  | 'trigger_feature'
  | 'map_center'
  | 'viewport_bounds'
  | 'active_preset'
  | 'active_filter_values'
  | 'current_time'
  | 'current_user'
  | 'current_account'
  | 'current_tenant';
export type MapExperienceBuiltinToolId =
  | 'select'
  | 'inspect'
  | 'draw_point'
  | 'draw_line'
  | 'draw_polygon'
  | 'measure_distance'
  | 'street_distance'
  | 'clear_drawings';
export type MapExperienceResolverFailurePolicy =
  | 'skip_step'
  | 'stop_pipeline'
  | 'continue_with_empty_result'
  | 'show_runtime_warning';
export type MapExperienceResolverAvailability =
  | 'available'
  | 'tenant_not_entitled'
  | 'connection_missing'
  | 'connection_not_ready'
  | 'user_forbidden'
  | 'layer_incompatible'
  | 'platform_disabled';
export type MapExperienceResolverConnectionStatus = 'ready' | 'needs_configuration' | 'disabled' | 'invalid';
export type MapExperienceResolverRendererKind =
  | 'record_summary'
  | 'notes_timeline'
  | 'activity_feed'
  | 'address_details'
  | 'related_items_table';
export type MapExperienceResolverInputBindingKind =
  | 'feature_field'
  | 'semantic_role'
  | 'builtin_value'
  | 'constant'
  | 'resolver_result';
export type MapExperienceSurfaceKind =
  | 'hover_popup'
  | 'side_panel'
  | 'bottom_sheet'
  | 'floating_summary_card'
  | 'ambiguous_selection_summary';
export type MapExperienceSurfaceBlockKind =
  | 'text'
  | 'badge_row'
  | 'stats_row'
  | 'details_grid'
  | 'list'
  | 'timeline'
  | 'action_row'
  | 'tabs'
  | 'custom'
  | 'empty_state'
  | 'loading_state'
  | 'error_state';
export type MapExperiencePipelineTriggerKind =
  | 'manual'
  | 'hover_enter'
  | 'hover_leave'
  | 'click'
  | 'context_menu'
  | 'tool_activated'
  | 'tool_deactivated'
  | 'selection_changed'
  | 'filter_value_changed'
  | 'preset_applied'
  | 'map_loaded'
  | 'location_updated'
  | 'drawing_started'
  | 'drawing_updated'
  | 'drawing_completed'
  | 'drawing_cleared';
export type MapExperienceTargetOperator =
  | 'self'
  | 'same_layer'
  | 'same_source'
  | 'related_by_field'
  | 'resolver_result'
  | 'spatial_radius'
  | 'viewport_contents'
  | 'within_drawn_geometry'
  | 'intersects_drawn_geometry';
export type MapExperienceConditionGroupOperator = 'all' | 'any' | 'not';
export type MapExperienceConditionOperator =
  | 'eq'
  | 'neq'
  | 'in'
  | 'not_in'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'between'
  | 'contains'
  | 'exists'
  | 'not_exists'
  | 'distance_from_reference_gt'
  | 'distance_from_reference_gte'
  | 'distance_from_reference_lt'
  | 'distance_from_reference_lte'
  | 'within_radius_of_reference'
  | 'outside_radius_of_reference';
export type MapExperiencePipelineStepKind = 'resolve' | 'target_set' | 'condition';
export type MapExperienceEffectKind =
  | 'open_surface'
  | 'close_surface'
  | 'bind_surface_data'
  | 'bind_feature_state'
  | 'highlight_set'
  | 'clear_highlight_set'
  | 'dim_others'
  | 'clear_dimming'
  | 'fly_to_feature'
  | 'fly_to_bounds'
  | 'open_route'
  | 'open_external_url'
  | 'copy_field'
  | 'set_runtime_filter_value'
  | 'set_runtime_filter_enabled'
  | 'select_feature_set'
  | 'clear_selection'
  | 'activate_builtin_tool'
  | 'deactivate_builtin_tool'
  | 'clear_drawings'
  | 'export_target_set_csv';

export interface MapExperienceToolRailDefinition {
  default_tool_id?: MapExperienceBuiltinToolId | null;
  visible_tool_ids: MapExperienceBuiltinToolId[];
}

export interface MapExperienceInteractivityFieldDefinition {
  field_key: string;
  label: string;
  value_type: MapExperienceInteractivityValueType;
  source_kind: MapExperienceInteractivityFieldSourceKind;
  supported_operators: string[];
  description?: string | null;
  enum_options?: MapExperienceFilterOption[] | null;
  semantic_roles?: string[] | null;
  display_format?: string | null;
  filter_safe?: boolean | null;
  interaction_safe?: boolean | null;
  sortable?: boolean | null;
  null_behavior?: string | null;
}

export interface MapExperienceInteractivitySourceCapabilities {
  selectable?: boolean | null;
  hoverable?: boolean | null;
  popupable?: boolean | null;
  inspectable?: boolean | null;
  highlightable?: boolean | null;
  filterable?: boolean | null;
  spatial_query_mode?: MapExperienceInteractivitySpatialQueryMode | null;
  supports_cluster_selection?: boolean | null;
  supports_related_lookup?: boolean | null;
  supports_compare_mode?: boolean | null;
  supports_summary_card?: boolean | null;
}

export interface MapExperienceInteractivitySourceDefinition {
  source_id: string;
  source_layer: string;
  label?: string | null;
  description?: string | null;
  geometry_kind?: MapExperienceInteractivityGeometryKind | null;
  identity: {
    feature_id_field: string;
  };
  fields: MapExperienceInteractivityFieldDefinition[];
  capabilities: MapExperienceInteractivitySourceCapabilities;
  semantic_roles?: Record<string, string> | null;
  relation_hints?: Array<Record<string, unknown>> | null;
  resolver_ids?: string[] | null;
  surface_ids?: string[] | null;
}

export interface MapExperienceBuiltinValueDefinition {
  value_id: MapExperienceBuiltinValueId;
  label: string;
  value_type: MapExperienceInteractivityValueType;
  description?: string | null;
  requires_capability?: string | null;
}

export interface MapExperienceResolverConnectionDefinition {
  resolver_connection_id: string;
  provider?: string | null;
  label?: string | null;
  scope_kind?: string | null;
  scope_id?: string | null;
  status?: MapExperienceResolverConnectionStatus | null;
  required_capabilities?: string[] | null;
}

export interface MapExperienceResolverDefinition {
  resolver_id: string;
  resolver_attachment_id?: string | null;
  resolver_type_id?: string | null;
  resolver_connection_id?: string | null;
  source_id?: string | null;
  source_layer?: string | null;
  label?: string | null;
  description?: string | null;
  provider?: string | null;
  availability?: MapExperienceResolverAvailability | null;
  availability_reason?: string | null;
  connection_status?: MapExperienceResolverConnectionStatus | null;
  input_schema?: Record<string, unknown> | null;
  output_schema?: Record<string, unknown> | null;
  timeout_ms?: number | null;
  cache_ttl_ms?: number | null;
  permissions?: string[] | null;
  required_permissions?: string[] | null;
  required_capabilities?: string[] | null;
  failure_policy?: MapExperienceResolverFailurePolicy | null;
  result_limit?: number | null;
  supported_renderer_kinds?: MapExperienceResolverRendererKind[] | null;
  default_renderer_kind?: MapExperienceResolverRendererKind | null;
  renderer_kind?: MapExperienceResolverRendererKind | null;
  required_inputs?: Array<{
    input_key: string;
    label?: string | null;
    description?: string | null;
    binding_kind?: MapExperienceResolverInputBindingKind | null;
    binding?: Record<string, unknown> | null;
    required?: boolean | null;
  }> | null;
  safe_overrides?: Record<string, unknown> | null;
}

export interface MapExperienceSurfaceBlockDefinition {
  id?: string | null;
  kind: MapExperienceSurfaceBlockKind;
  label?: string | null;
  binding?: Record<string, unknown> | null;
  items?: Array<Record<string, unknown>> | null;
  empty_state?: string | null;
}

export interface MapExperienceSurfaceTabDefinition {
  tab_id: string;
  label: string;
  sections: MapExperienceSurfaceBlockDefinition[];
}

export interface MapExperienceSurfaceDefinition {
  surface_id: string;
  source_id?: string | null;
  source_layer?: string | null;
  surface_kind: MapExperienceSurfaceKind;
  label?: string | null;
  title_binding?: Record<string, unknown> | null;
  data_binding?: Record<string, unknown> | null;
  sections: MapExperienceSurfaceBlockDefinition[];
  tabs?: MapExperienceSurfaceTabDefinition[] | null;
}

export interface MapExperienceTargetSelectorDefinition {
  selector_id?: string | null;
  operator: MapExperienceTargetOperator;
  mode?: MapExperienceInteractivitySpatialQueryMode | null;
  reference?: Record<string, unknown> | null;
  target?: {
    source_id?: string | null;
    source_layer?: string | null;
  } | null;
  relation?: {
    left_field?: string | null;
    right_field?: string | null;
  } | null;
  radius_m?: number | null;
  limit?: number | null;
  resolver_id?: string | null;
}

export interface MapExperienceConditionRuleDefinition {
  field_ref?: Record<string, unknown> | null;
  operator: MapExperienceConditionOperator;
  value?: unknown;
  reference?: Record<string, unknown> | null;
  radius_m?: number | null;
}

export interface MapExperienceConditionGroupDefinition {
  operator: MapExperienceConditionGroupOperator;
  rules: Array<MapExperienceConditionGroupDefinition | MapExperienceConditionRuleDefinition>;
}

export interface MapExperiencePipelineResolveStepDefinition {
  step_id: string;
  kind: 'resolve';
  resolver_id: string;
  resolver_attachment_id?: string | null;
  resolver_type_id?: string | null;
  input?: Record<string, unknown> | null;
  input_mappings?: Array<{
    input_key: string;
    binding_kind: MapExperienceResolverInputBindingKind;
    binding?: Record<string, unknown> | null;
    required?: boolean | null;
  }> | null;
  failure_policy?: MapExperienceResolverFailurePolicy | null;
}

export interface MapExperiencePipelineTargetSetStepDefinition {
  step_id: string;
  kind: 'target_set';
  input?: string | null;
  selector: MapExperienceTargetSelectorDefinition;
}

export interface MapExperiencePipelineConditionStepDefinition {
  step_id: string;
  kind: 'condition';
  input?: string | null;
  target?: MapExperienceTargetSelectorDefinition | null;
  logic: MapExperienceConditionGroupDefinition;
}

export type MapExperiencePipelineStepDefinition =
  | MapExperiencePipelineResolveStepDefinition
  | MapExperiencePipelineTargetSetStepDefinition
  | MapExperiencePipelineConditionStepDefinition;

export interface MapExperienceEffectDefinition {
  effect_id?: string | null;
  kind: MapExperienceEffectKind;
  input?: string | Record<string, unknown> | null;
  surface_id?: string | null;
  tool_id?: MapExperienceBuiltinToolId | null;
  style?: string | null;
  filter_id?: string | null;
  value?: unknown;
  auto_clear_ms?: number | null;
}

export interface MapExperiencePipelineDefinition {
  pipeline_id: string;
  source_id?: string | null;
  source_layer?: string | null;
  label?: string | null;
  trigger: {
    kind: MapExperiencePipelineTriggerKind;
    source?: string | null;
    geometry_kind?: 'point' | 'line' | 'polygon' | null;
  };
  steps: MapExperiencePipelineStepDefinition[];
  effects: MapExperienceEffectDefinition[];
  stop_propagation?: boolean | null;
}

export interface MapExperienceInteractivityDefinition {
  sources: MapExperienceInteractivitySourceDefinition[];
  builtin_values: MapExperienceBuiltinValueDefinition[];
  resolvers: MapExperienceResolverDefinition[];
  surfaces: MapExperienceSurfaceDefinition[];
  pipelines: MapExperiencePipelineDefinition[];
}

export interface MapExperienceMetadataV1 {
  version: 1 | 2;
  style_profiles: MapExperienceStyleProfile[];
  active_style_profiles: Record<string, string>;
  render_stacks?: MapExperienceRenderStackDefinition[];
  filter_sets: MapExperienceFilterSet[];
  active_filter_values: Record<string, Record<string, unknown>>;
  presets: MapExperiencePresetDefinition[];
  selected_preset_id?: string | null;
  tools?: MapExperienceToolRailDefinition | null;
  interactivity?: MapExperienceInteractivityDefinition | null;
}

const DEFAULT_SINGLE_COLOR = 'rgb(59 130 246)';
const DEFAULT_RULE_LEGEND_COLOR = 'rgb(71 85 105)';
const MAP_EXPERIENCE_SEMANTIC_COLOR_TOKENS = new Set<MapExperienceSemanticColorToken>([
  'foreground',
  'muted-foreground',
  'card-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'map-label',
  'map-border',
  'map-background',
  'map-road',
  'map-water',
  'map-landuse',
]);
const LEGACY_STYLE_COLOR_VAR_MAP: Record<string, MapExperienceSemanticColorToken> = {
  'var(--foreground)': 'foreground',
  'var(--muted-foreground)': 'muted-foreground',
  'var(--card-foreground)': 'card-foreground',
  'var(--primary)': 'primary',
  'var(--primary-foreground)': 'primary-foreground',
  'var(--secondary)': 'secondary',
  'var(--secondary-foreground)': 'secondary-foreground',
  'var(--accent)': 'accent',
  'var(--accent-foreground)': 'accent-foreground',
  'var(--destructive)': 'destructive',
  'var(--destructive-foreground)': 'destructive-foreground',
  'var(--map-label)': 'map-label',
  'var(--map-border)': 'map-border',
  'var(--map-background)': 'map-background',
  'var(--map-road)': 'map-road',
  'var(--map-water)': 'map-water',
  'var(--map-landuse)': 'map-landuse',
};
const STYLE_COLOR_PROP_NAMES = new Set<string>([
  'fill-color',
  'fill-outline-color',
  'line-color',
  'circle-color',
  'circle-stroke-color',
  'icon-color',
  'icon-halo-color',
  'text-color',
  'text-halo-color',
  'fill-extrusion-color',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const toArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const buildTargetKey = (sourceId: string, sourceLayer: string): TargetKey => `${sourceId}::${sourceLayer}`;

const buildTargetKeys = (
  sourceId: string,
  sourceLayer: string,
  sourceAlias?: string | null,
): TargetKey[] => {
  const keys = new Set<TargetKey>();
  const normalizedSourceId = sourceId.trim();
  const normalizedSourceLayer = sourceLayer.trim();
  const normalizedSourceAlias = sourceAlias?.trim() ?? '';
  if (normalizedSourceId && normalizedSourceLayer) {
    keys.add(buildTargetKey(normalizedSourceId, normalizedSourceLayer));
  }
  if (normalizedSourceAlias && normalizedSourceLayer) {
    keys.add(buildTargetKey(normalizedSourceAlias, normalizedSourceLayer));
  }
  return Array.from(keys);
};

const matchesExperienceSourceId = (
  experienceSourceId: string,
  targetSourceId: string,
  targetSourceAlias?: string | null,
) => {
  const normalizedExperienceSourceId = experienceSourceId.trim();
  const normalizedTargetSourceId = targetSourceId.trim();
  const normalizedTargetSourceAlias = targetSourceAlias?.trim() ?? '';
  return (
    normalizedExperienceSourceId !== '' &&
    (
      normalizedExperienceSourceId === normalizedTargetSourceId ||
      normalizedExperienceSourceId === normalizedTargetSourceAlias
    )
  );
};

const normalizeStringRecord = (value: unknown): Record<string, string> => {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => typeof entry === 'string' && entry.trim())
      .map(([key, entry]) => [key, (entry as string).trim()])
  );
};

const FIELD_TOKEN_PATTERN = /\{([a-zA-Z0-9_.-]+)\}/g;

const buildTextFieldTemplateExpression = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  const segments: Array<{ kind: 'text'; value: string } | { kind: 'field'; value: string }> = [];
  let cursor = 0;
  let match: RegExpExecArray | null = null;

  FIELD_TOKEN_PATTERN.lastIndex = 0;
  while ((match = FIELD_TOKEN_PATTERN.exec(value)) !== null) {
    const [token, fieldName] = match;
    if (match.index > cursor) {
      segments.push({ kind: 'text', value: value.slice(cursor, match.index) });
    }
    segments.push({ kind: 'field', value: fieldName });
    cursor = match.index + token.length;
  }

  if (cursor < value.length) {
    segments.push({ kind: 'text', value: value.slice(cursor) });
  }

  if (segments.length === 0 || !segments.some(segment => segment.kind === 'field')) {
    return value;
  }

  if (segments.length === 1 && segments[0]?.kind === 'field') {
    return ['get', segments[0].value];
  }

  return [
    'concat',
    ...segments.map(segment =>
      segment.kind === 'field'
        ? ['to-string', ['coalesce', ['get', segment.value], '']]
        : segment.value,
    ),
  ];
};

const cloneJsonValue = <T,>(value: T): T =>
  typeof structuredClone === 'function' ? structuredClone(value) : (JSON.parse(JSON.stringify(value)) as T);

const ensureString = (value: unknown, fallback = '') => (typeof value === 'string' ? value : fallback);

export const isMapExperienceSemanticColorRef = (value: unknown): value is MapExperienceSemanticColorRef =>
  isRecord(value) && value.kind === 'semantic' && typeof value.token === 'string' && MAP_EXPERIENCE_SEMANTIC_COLOR_TOKENS.has(value.token as MapExperienceSemanticColorToken);

const normalizeLegacyStyleColorString = (value: string): MapExperienceStyleColorValue => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (MAP_EXPERIENCE_SEMANTIC_COLOR_TOKENS.has(trimmed as MapExperienceSemanticColorToken)) {
    return {
      kind: 'semantic',
      token: trimmed as MapExperienceSemanticColorToken,
    };
  }
  const legacySemanticToken = LEGACY_STYLE_COLOR_VAR_MAP[trimmed.replace(/\s+/g, '')];
  if (legacySemanticToken) {
    return {
      kind: 'semantic',
      token: legacySemanticToken,
    };
  }
  return trimmed;
};

export const normalizeOptionalMapExperienceColorValue = (value: unknown): MapExperienceStyleColorValue | null => {
  if (typeof value === 'string') {
    const normalized = normalizeLegacyStyleColorString(value);
    return typeof normalized === 'string' ? (normalized || null) : normalized;
  }
  if (isMapExperienceSemanticColorRef(value)) {
    return {
      kind: 'semantic',
      token: value.token,
    };
  }
  return null;
};

const normalizeRequiredMapExperienceColorValue = (
  value: unknown,
  fallback = DEFAULT_SINGLE_COLOR,
): MapExperienceStyleColorValue => normalizeOptionalMapExperienceColorValue(value) ?? fallback;

export const resolveMapExperienceColorValue = (
  value: MapExperienceStyleColorValue | null | undefined,
  themeColors?: MapExperienceThemeColorPalette | null,
  fallback = DEFAULT_SINGLE_COLOR,
): string => {
  if (!value) {
    return fallback;
  }
  if (typeof value === 'string') {
    const normalized = normalizeLegacyStyleColorString(value);
    if (typeof normalized === 'string') {
      return normalized || fallback;
    }
    return themeColors?.[normalized.token]?.trim() || fallback;
  }
  return themeColors?.[value.token]?.trim() || fallback;
};

export type MapExperienceCompileColorMode = 'literal' | 'global-state';

const resolveCompiledMapExperienceColorValue = (
  value: MapExperienceStyleColorValue | null | undefined,
  themeColors: MapExperienceThemeColorPalette | null | undefined,
  colorMode: MapExperienceCompileColorMode,
  globalStateDefaults: Map<MapExperienceSemanticColorToken, string>,
  fallback = DEFAULT_SINGLE_COLOR,
): string | Expr => {
  const normalizedValue =
    typeof value === 'string'
      ? normalizeLegacyStyleColorString(value)
      : value;
  if (!normalizedValue) {
    return fallback;
  }
  if (colorMode !== 'global-state' || typeof normalizedValue === 'string') {
    return resolveMapExperienceColorValue(normalizedValue, themeColors, fallback);
  }
  const resolvedDefault = resolveMapExperienceColorValue(normalizedValue, themeColors, fallback);
  globalStateDefaults.set(normalizedValue.token, resolvedDefault);
  return ['coalesce', ['global-state', normalizedValue.token], resolvedDefault];
};

const mergeCompiledMapExperienceStateDefaults = (
  styleJson: Record<string, unknown>,
  defaults: Map<MapExperienceSemanticColorToken, string>,
): Record<string, unknown> => {
  if (defaults.size === 0) {
    return styleJson;
  }
  const nextState = isRecord(styleJson.state) ? { ...(styleJson.state as Record<string, unknown>) } : {};
  defaults.forEach((defaultValue, token) => {
    const existingEntry = isRecord(nextState[token]) ? { ...(nextState[token] as Record<string, unknown>) } : {};
    nextState[token] = {
      ...existingEntry,
      default: defaultValue,
    };
  });
  return {
    ...styleJson,
    state: nextState,
  };
};

const normalizeStyleOverrideColorValue = (value: unknown) => {
  const normalized = normalizeOptionalMapExperienceColorValue(value);
  return normalized ?? cloneJsonValue(value);
};

const normalizeStyleRepresentation = (value: unknown): MapExperienceStyleRepresentation | null => {
  switch (value) {
    case 'fill':
    case 'line':
    case 'circle':
    case 'symbol':
    case 'fill-extrusion':
      return value;
    default:
      return null;
  }
};

const normalizeOptionalIconImage = (value: unknown) => {
  const iconImage = ensureString(value).trim();
  return iconImage || null;
};

const normalizeStyleOverrideRecord = (value: unknown): MapExperienceStyleOverrideRecord | null => {
  if (!isRecord(value)) return null;
  const next = normalizeMapLibreLayoutOverrides(
    normalizeMapLibrePaintOverrides(value as MapExperienceStyleOverrideRecord),
  );
  if (!next) {
    return null;
  }
  Object.entries(next).forEach(([prop, entryValue]) => {
    if (!STYLE_COLOR_PROP_NAMES.has(prop)) {
      return;
    }
    next[prop] = normalizeStyleOverrideColorValue(entryValue);
  });
  return next;
};

const normalizeStyleInteractionConfig = (value: unknown): MapExperienceStyleInteractionConfig | null => {
  if (!isRecord(value)) return null;
  const hitTargetPx = value.hit_target_px == null ? null : Number(value.hit_target_px);
  return {
    hit_target_px: Number.isFinite(hitTargetPx) && hitTargetPx >= 0 ? hitTargetPx : null,
  };
};

const readOverrideIconImage = (layout: MapExperienceStyleOverrideRecord | null) =>
  normalizeOptionalIconImage(layout?.['icon-image']);

const normalizeSingleProfile = (value: unknown): MapExperienceSingleStyleProfile | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  const sourceId = ensureString(value.source_id).trim();
  const sourceLayer = ensureString(value.source_layer).trim();
  if (!id || !sourceId || !sourceLayer) return null;
  const layout = normalizeStyleOverrideRecord(value.layout);
  return {
    id,
    source_id: sourceId,
    source_layer: sourceLayer,
    kind: 'single',
    representation: normalizeStyleRepresentation(value.representation),
    label: ensureString(value.label).trim() || null,
    interaction: normalizeStyleInteractionConfig(value.interaction),
    color: normalizeRequiredMapExperienceColorValue(value.color),
    legend_label: ensureString(value.legend_label).trim() || null,
    icon_image: normalizeOptionalIconImage(value.icon_image) ?? readOverrideIconImage(layout),
    paint: normalizeStyleOverrideRecord(value.paint),
    layout,
  };
};

const normalizeCategoricalProfile = (value: unknown): MapExperienceCategoricalStyleProfile | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  const sourceId = ensureString(value.source_id).trim();
  const sourceLayer = ensureString(value.source_layer).trim();
  const field = ensureString(value.field).trim();
  if (!id || !sourceId || !sourceLayer || !field) return null;
  const categories = toArray<Record<string, unknown>>(value.categories)
    .map(entry => {
      const categoryValue = ensureString(entry?.value).trim();
      if (!categoryValue) return null;
      const layout = normalizeStyleOverrideRecord(entry?.layout);
      return {
        value: categoryValue,
        label: ensureString(entry?.label).trim() || categoryValue,
        color: normalizeRequiredMapExperienceColorValue(entry?.color),
        hidden: entry?.hidden === true,
        icon_image: normalizeOptionalIconImage(entry?.icon_image) ?? readOverrideIconImage(layout),
        paint: normalizeStyleOverrideRecord(entry?.paint),
        layout,
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
  return {
    id,
    source_id: sourceId,
    source_layer: sourceLayer,
    kind: 'categorical',
    representation: normalizeStyleRepresentation(value.representation),
    label: ensureString(value.label).trim() || null,
    interaction: normalizeStyleInteractionConfig(value.interaction),
    field,
    categories,
    fallback_color: normalizeOptionalMapExperienceColorValue(value.fallback_color),
  };
};

const normalizeRangeProfile = (value: unknown): MapExperienceRangeStyleProfile | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  const sourceId = ensureString(value.source_id).trim();
  const sourceLayer = ensureString(value.source_layer).trim();
  const field = ensureString(value.field).trim();
  if (!id || !sourceId || !sourceLayer || !field) return null;
  const breaks = toArray<Record<string, unknown>>(value.breaks)
    .map(entry => {
      const label = ensureString(entry?.label).trim();
      if (!label) return null;
      const min = entry?.min == null ? null : Number(entry.min);
      const max = entry?.max == null ? null : Number(entry.max);
      const layout = normalizeStyleOverrideRecord(entry?.layout);
      return {
        id: ensureString(entry?.id).trim() || undefined,
        label,
        color: normalizeRequiredMapExperienceColorValue(entry?.color),
        min: Number.isFinite(min) ? min : null,
        max: Number.isFinite(max) ? max : null,
        hidden: entry?.hidden === true,
        icon_image: normalizeOptionalIconImage(entry?.icon_image) ?? readOverrideIconImage(layout),
        paint: normalizeStyleOverrideRecord(entry?.paint),
        layout,
      } satisfies MapExperienceRangeBreak;
    })
    .filter((entry): entry is MapExperienceRangeBreak => Boolean(entry));
  return {
    id,
    source_id: sourceId,
    source_layer: sourceLayer,
    kind: 'range',
    representation: normalizeStyleRepresentation(value.representation),
    label: ensureString(value.label).trim() || null,
    interaction: normalizeStyleInteractionConfig(value.interaction),
    field,
    breaks,
    fallback_color: normalizeOptionalMapExperienceColorValue(value.fallback_color),
  };
};

const normalizeStoredLayerRule = (value: unknown): LayerRuleDefinition | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  if (!id) return null;
  const logic = isRecord(value.logic) ? (cloneJsonValue(value.logic as StyleRuleLogic) as StyleRuleLogic) : null;
  return {
    id,
    enabled: value.enabled !== false,
    label: ensureString(value.label).trim() || '',
    color: ensureString(value.color).trim() || DEFAULT_SINGLE_COLOR,
    logic: ensureLogicRoot(logic ?? createGroup()),
    representation: normalizeStyleRepresentation(value.representation),
    paint: normalizeStyleOverrideRecord(value.paint),
    layout: normalizeStyleOverrideRecord(value.layout),
  };
};

const normalizeRulesProfile = (value: unknown): MapExperienceRulesStyleProfile | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  const sourceId = ensureString(value.source_id).trim();
  const sourceLayer = ensureString(value.source_layer).trim();
  if (!id || !sourceId || !sourceLayer) return null;
  const rules = toArray(value.rules).map(normalizeStoredLayerRule).filter((entry): entry is LayerRuleDefinition => Boolean(entry));
  const legacyRuleRepresentation = toArray(value.rules)
    .map(entry => (isRecord(entry) ? normalizeStyleRepresentation(entry.representation) : null))
    .find((entry): entry is NonNullable<typeof entry> => entry != null)
    ?? null;
  return {
    id,
    source_id: sourceId,
    source_layer: sourceLayer,
    kind: 'rules',
    representation: normalizeStyleRepresentation(value.representation) ?? legacyRuleRepresentation,
    label: ensureString(value.label).trim() || null,
    legend_label: ensureString(value.legend_label).trim() || null,
    interaction: normalizeStyleInteractionConfig(value.interaction),
    layout: normalizeStyleOverrideRecord(value.layout),
    mode: 'hide_non_matching',
    rules,
  };
};

const normalizeStyleProfile = (value: unknown): MapExperienceStyleProfile | null => {
  if (!isRecord(value)) return null;
  switch (value.kind) {
    case 'single':
      return normalizeSingleProfile(value);
    case 'categorical':
      return normalizeCategoricalProfile(value);
    case 'range':
      return normalizeRangeProfile(value);
    case 'rules':
      return normalizeRulesProfile(value);
    default:
      return null;
  }
};

const normalizeRenderStackRole = (value: unknown): MapExperienceRenderStackRole | null => {
  switch (value) {
    case 'base':
    case 'overlay_badge':
    case 'overlay_label':
    case 'overlay_symbol':
      return value;
    default:
      return null;
  }
};

const normalizeStyleRuleLogic = (value: unknown): StyleRuleLogic | null => {
  if (!isRecord(value)) return null;
  if (value.type === 'condition') {
    const id = ensureString(value.id).trim();
    if (!id) return null;
    return {
      id,
      type: 'condition',
      fieldId: ensureString(value.fieldId).trim(),
      operatorId: ensureString(value.operatorId).trim() || 'eq',
      values: Array.isArray(value.values) ? value.values.map(entry => String(entry)) : [],
    };
  }
  if (value.type === 'group') {
    const id = ensureString(value.id).trim();
    if (!id) return null;
    return {
      id,
      type: 'group',
      operator: value.operator === 'OR' ? 'OR' : 'AND',
      children: toArray(value.children).map(normalizeStyleRuleLogic).filter((entry): entry is StyleRuleLogic => Boolean(entry)),
    };
  }
  return null;
};

const normalizeRenderStackEntry = (value: unknown): MapExperienceRenderStackEntry | null => {
  if (!isRecord(value)) return null;
  const entryId = ensureString(value.entry_id).trim();
  const profileId = ensureString(value.profile_id).trim();
  if (!entryId || !profileId) return null;
  return {
    entry_id: entryId,
    profile_id: profileId,
    enabled: value.enabled === false ? false : true,
    label: ensureString(value.label).trim() || null,
    stack_role: normalizeRenderStackRole(value.stack_role),
    filter_logic: normalizeStyleRuleLogic(value.filter_logic),
  };
};

const normalizeRenderStack = (value: unknown): MapExperienceRenderStackDefinition | null => {
  if (!isRecord(value)) return null;
  const sourceId = ensureString(value.source_id).trim();
  const sourceLayer = ensureString(value.source_layer).trim();
  if (!sourceId || !sourceLayer) return null;
  return {
    source_id: sourceId,
    source_layer: sourceLayer,
    entries: toArray(value.entries).map(normalizeRenderStackEntry).filter((entry): entry is MapExperienceRenderStackEntry => Boolean(entry)),
  };
};

const normalizeFilterDefinition = (value: unknown): MapExperienceFilterDefinition | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  const field = ensureString(value.field).trim();
  const kind = ensureString(value.kind).trim() as MapExperienceFilterKind;
  if (!id || !field) return null;
  if (!['boolean', 'single_select', 'multi_select', 'numeric_range', 'date_range', 'text_contains'].includes(kind)) {
    return null;
  }
  const options = toArray<Record<string, unknown>>(value.options)
    .map(entry => {
      const optionValue = ensureString(entry?.value).trim();
      if (!optionValue) return null;
      return {
        value: optionValue,
        label: ensureString(entry?.label).trim() || optionValue,
      } satisfies MapExperienceFilterOption;
    })
    .filter((entry): entry is MapExperienceFilterOption => Boolean(entry));
  return {
    id,
    field,
    kind,
    operator: normalizeFilterOperator(value.operator, kind),
    value: normalizeFilterComparisonValue(kind, value.operator, value.value),
    label: ensureString(value.label).trim() || null,
    description: ensureString(value.description).trim() || null,
    editable_in_view: value.editable_in_view === false ? false : true,
    options,
    placeholder: ensureString(value.placeholder).trim() || null,
    viewer_presentation:
      ensureString(value.viewer_presentation).trim() === 'max_slider'
        ? 'max_slider'
        : 'range',
    min: normalizeNumericFilterBound(value.min),
    max: normalizeNumericFilterBound(value.max),
    step: normalizeNumericFilterBound(value.step),
    default_value: normalizeFilterRuntimeValue(kind, value.default_value),
    server_binding_key: ensureString(value.server_binding_key).trim() || null,
    server_execution:
      value.server_execution === 'client_only' || value.server_execution === 'server_only'
        ? value.server_execution
        : 'auto',
  };
};

const normalizeFilterSet = (value: unknown): MapExperienceFilterSet | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  const sourceId = ensureString(value.source_id).trim();
  const sourceLayer = ensureString(value.source_layer).trim();
  if (!id || !sourceId || !sourceLayer) return null;
  return {
    id,
    source_id: sourceId,
    source_layer: sourceLayer,
    label: ensureString(value.label).trim() || null,
    filters: toArray(value.filters).map(normalizeFilterDefinition).filter((entry): entry is MapExperienceFilterDefinition => Boolean(entry)),
  };
};

const normalizeActiveFilterValues = (value: unknown): Record<string, Record<string, unknown>> => {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => isRecord(entry))
      .map(([key, entry]) => [key, cloneJsonValue(entry as Record<string, unknown>)])
  );
};

const normalizeNumericFilterBound = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeFilterOperator = (
  value: unknown,
  kind: MapExperienceFilterKind,
): MapExperienceFilterOperator | null => {
  if (kind !== 'boolean') return null;
  switch (value) {
    case 'equals':
    case 'not_equals':
    case 'exists':
    case 'not_exists':
    case 'greater_than':
    case 'less_than':
      return value;
    default:
      return 'equals';
  }
};

const normalizeFilterComparisonValue = (
  kind: MapExperienceFilterKind,
  operatorInput: unknown,
  value: unknown,
): string | number | boolean | null => {
  if (kind !== 'boolean') return null;
  const operator = normalizeFilterOperator(operatorInput, kind) ?? 'equals';
  if (operator === 'exists' || operator === 'not_exists') {
    return null;
  }
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = ensureString(value).trim();
  if (!text) {
    return operator === 'equals' ? true : null;
  }
  if (text === 'true') return true;
  if (text === 'false') return false;
  if (operator === 'greater_than' || operator === 'less_than') {
    const numeric = Number(text);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return text;
};

const normalizeFilterRuntimeValue = (kind: MapExperienceFilterKind, value: unknown): unknown => {
  switch (kind) {
    case 'boolean':
      return typeof value === 'boolean' ? value : null;
    case 'single_select':
    case 'text_contains': {
      const next = ensureString(value).trim();
      return next || null;
    }
    case 'multi_select': {
      const next = toArray(value)
        .map(entry => ensureString(entry).trim())
        .filter(Boolean);
      return next.length > 0 ? next : [];
    }
    case 'numeric_range': {
      if (!isRecord(value)) return { min: null, max: null };
      return {
        min: normalizeNumericFilterBound(value.min),
        max: normalizeNumericFilterBound(value.max),
      };
    }
    case 'date_range': {
      if (!isRecord(value)) return { min: null, max: null };
      return {
        min: ensureString(value.min).trim() || null,
        max: ensureString(value.max).trim() || null,
      };
    }
    default:
      return null;
  }
};

const normalizePreset = (value: unknown): MapExperiencePresetDefinition | null => {
  if (!isRecord(value)) return null;
  const id = ensureString(value.id).trim();
  const label = ensureString(value.label).trim();
  if (!id || !label) return null;
  const viewport = isRecord(value.viewport) ? (cloneJsonValue(value.viewport as MapExperienceViewState) as MapExperienceViewState) : null;
  return {
    id,
    label,
    viewport,
    layer_visibility: isRecord(value.layer_visibility)
      ? Object.fromEntries(
          Object.entries(value.layer_visibility)
            .filter(([, entry]) => typeof entry === 'boolean')
            .map(([key, entry]) => [key, entry as boolean])
        )
      : null,
    active_style_profiles: normalizeStringRecord(value.active_style_profiles),
    active_filter_values: normalizeActiveFilterValues(value.active_filter_values),
  };
};

const normalizeStringArray = (value: unknown) =>
  toArray<string>(value)
    .map(entry => ensureString(entry).trim())
    .filter(Boolean);

const normalizeInteractivityGeometryKind = (value: unknown): MapExperienceInteractivityGeometryKind | null => {
  switch (value) {
    case 'point':
    case 'line':
    case 'polygon':
    case 'mixed':
    case 'unknown':
      return value;
    default:
      return null;
  }
};

const normalizeInteractivityValueType = (value: unknown): MapExperienceInteractivityValueType | null => {
  switch (value) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'date':
    case 'enum':
    case 'geometry':
    case 'feature_ref':
    case 'feature_ref_list':
      return value;
    default:
      return null;
  }
};

const normalizeInteractivityFieldSourceKind = (value: unknown): MapExperienceInteractivityFieldSourceKind | null => {
  switch (value) {
    case 'feature_property':
    case 'enrichment':
    case 'resolver_output':
    case 'builtin':
      return value;
    default:
      return null;
  }
};

const normalizeSpatialQueryMode = (value: unknown): MapExperienceInteractivitySpatialQueryMode | null => {
  switch (value) {
    case 'none':
    case 'rendered_only':
    case 'visible_only':
    case 'dataset_wide':
      return value;
    default:
      return null;
  }
};

const normalizeBuiltinValueId = (value: unknown): MapExperienceBuiltinValueId | null => {
  switch (value) {
    case 'my_location':
    case 'selected_feature':
    case 'hovered_feature':
    case 'trigger_feature':
    case 'map_center':
    case 'viewport_bounds':
    case 'active_preset':
    case 'active_filter_values':
    case 'current_time':
    case 'current_user':
    case 'current_account':
    case 'current_tenant':
      return value;
    default:
      return null;
  }
};

const normalizeBuiltinToolId = (value: unknown): MapExperienceBuiltinToolId | null => {
  switch (value) {
    case 'select':
    case 'inspect':
    case 'draw_point':
    case 'draw_line':
    case 'draw_polygon':
    case 'measure_distance':
    case 'street_distance':
    case 'clear_drawings':
      return value;
    default:
      return null;
  }
};

const normalizeResolverFailurePolicy = (value: unknown): MapExperienceResolverFailurePolicy | null => {
  switch (value) {
    case 'skip_step':
    case 'stop_pipeline':
    case 'continue_with_empty_result':
    case 'show_runtime_warning':
      return value;
    default:
      return null;
  }
};

const normalizeResolverAvailability = (value: unknown): MapExperienceResolverAvailability | null => {
  switch (value) {
    case 'available':
    case 'tenant_not_entitled':
    case 'connection_missing':
    case 'connection_not_ready':
    case 'user_forbidden':
    case 'layer_incompatible':
    case 'platform_disabled':
      return value;
    default:
      return null;
  }
};

const normalizeResolverConnectionStatus = (value: unknown): MapExperienceResolverConnectionStatus | null => {
  switch (value) {
    case 'ready':
    case 'needs_configuration':
    case 'disabled':
    case 'invalid':
      return value;
    default:
      return null;
  }
};

const normalizeResolverRendererKind = (value: unknown): MapExperienceResolverRendererKind | null => {
  switch (value) {
    case 'record_summary':
    case 'notes_timeline':
    case 'activity_feed':
    case 'address_details':
    case 'related_items_table':
      return value;
    default:
      return null;
  }
};

const normalizeResolverInputBindingKind = (value: unknown): MapExperienceResolverInputBindingKind | null => {
  switch (value) {
    case 'feature_field':
    case 'semantic_role':
    case 'builtin_value':
    case 'constant':
    case 'resolver_result':
      return value;
    default:
      return null;
  }
};

const normalizeSurfaceKind = (value: unknown): MapExperienceSurfaceKind | null => {
  switch (value) {
    case 'hover_popup':
    case 'side_panel':
    case 'bottom_sheet':
    case 'floating_summary_card':
    case 'ambiguous_selection_summary':
      return value;
    default:
      return null;
  }
};

const normalizeSurfaceBlockKind = (value: unknown): MapExperienceSurfaceBlockKind | null => {
  switch (value) {
    case 'text':
    case 'badge_row':
    case 'stats_row':
    case 'details_grid':
    case 'list':
    case 'timeline':
    case 'action_row':
    case 'tabs':
    case 'custom':
    case 'empty_state':
    case 'loading_state':
    case 'error_state':
      return value;
    default:
      return null;
  }
};

const normalizePipelineTriggerKind = (value: unknown): MapExperiencePipelineTriggerKind | null => {
  switch (value) {
    case 'manual':
    case 'hover_enter':
    case 'hover_leave':
    case 'click':
    case 'context_menu':
    case 'tool_activated':
    case 'tool_deactivated':
    case 'selection_changed':
    case 'filter_value_changed':
    case 'preset_applied':
    case 'map_loaded':
    case 'location_updated':
    case 'drawing_started':
    case 'drawing_updated':
    case 'drawing_completed':
    case 'drawing_cleared':
      return value;
    default:
      return null;
  }
};

const normalizeTargetOperator = (value: unknown): MapExperienceTargetOperator | null => {
  switch (value) {
    case 'self':
    case 'same_layer':
    case 'same_source':
    case 'related_by_field':
    case 'resolver_result':
    case 'spatial_radius':
    case 'viewport_contents':
    case 'within_drawn_geometry':
    case 'intersects_drawn_geometry':
      return value;
    default:
      return null;
  }
};

const normalizeConditionGroupOperator = (value: unknown): MapExperienceConditionGroupOperator =>
  value === 'any' || value === 'not' ? value : 'all';

const normalizeConditionOperator = (value: unknown): MapExperienceConditionOperator | null => {
  switch (value) {
    case 'eq':
    case 'neq':
    case 'in':
    case 'not_in':
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte':
    case 'between':
    case 'contains':
    case 'exists':
    case 'not_exists':
    case 'distance_from_reference_gt':
    case 'distance_from_reference_gte':
    case 'distance_from_reference_lt':
    case 'distance_from_reference_lte':
    case 'within_radius_of_reference':
    case 'outside_radius_of_reference':
      return value;
    default:
      return null;
  }
};

const normalizeEffectKind = (value: unknown): MapExperienceEffectKind | null => {
  switch (value) {
    case 'open_surface':
    case 'close_surface':
    case 'bind_surface_data':
    case 'bind_feature_state':
    case 'highlight_set':
    case 'clear_highlight_set':
    case 'dim_others':
    case 'clear_dimming':
    case 'fly_to_feature':
    case 'fly_to_bounds':
    case 'open_route':
    case 'open_external_url':
    case 'copy_field':
    case 'set_runtime_filter_value':
    case 'set_runtime_filter_enabled':
    case 'select_feature_set':
    case 'clear_selection':
    case 'activate_builtin_tool':
    case 'deactivate_builtin_tool':
    case 'clear_drawings':
    case 'export_target_set_csv':
      return value;
    default:
      return null;
  }
};

const normalizeInteractivityFieldDefinition = (value: unknown): MapExperienceInteractivityFieldDefinition | null => {
  if (!isRecord(value)) return null;
  const fieldKey = ensureString(value.field_key).trim();
  const label = ensureString(value.label).trim();
  const valueType = normalizeInteractivityValueType(value.value_type);
  const sourceKind = normalizeInteractivityFieldSourceKind(value.source_kind);
  if (!fieldKey || !label || !valueType || !sourceKind) return null;
  return {
    field_key: fieldKey,
    label,
    value_type: valueType,
    source_kind: sourceKind,
    supported_operators: normalizeStringArray(value.supported_operators),
    description: ensureString(value.description).trim() || null,
    enum_options: toArray<Record<string, unknown>>(value.enum_options)
      .map(option => {
        const optionValue = ensureString(option.value).trim();
        if (!optionValue) return null;
        return {
          value: optionValue,
          label: ensureString(option.label).trim() || null,
        } satisfies MapExperienceFilterOption;
      })
      .filter((entry): entry is MapExperienceFilterOption => Boolean(entry)),
    semantic_roles: normalizeStringArray(value.semantic_roles),
    display_format: ensureString(value.display_format).trim() || null,
    filter_safe: typeof value.filter_safe === 'boolean' ? value.filter_safe : null,
    interaction_safe: typeof value.interaction_safe === 'boolean' ? value.interaction_safe : null,
    sortable: typeof value.sortable === 'boolean' ? value.sortable : null,
    null_behavior: ensureString(value.null_behavior).trim() || null,
  };
};

const normalizeInteractivitySourceCapabilities = (
  value: unknown,
): MapExperienceInteractivitySourceCapabilities => {
  if (!isRecord(value)) {
    return { spatial_query_mode: null };
  }
  return {
    selectable: typeof value.selectable === 'boolean' ? value.selectable : null,
    hoverable: typeof value.hoverable === 'boolean' ? value.hoverable : null,
    popupable: typeof value.popupable === 'boolean' ? value.popupable : null,
    inspectable: typeof value.inspectable === 'boolean' ? value.inspectable : null,
    highlightable: typeof value.highlightable === 'boolean' ? value.highlightable : null,
    filterable: typeof value.filterable === 'boolean' ? value.filterable : null,
    spatial_query_mode: normalizeSpatialQueryMode(value.spatial_query_mode),
    supports_cluster_selection:
      typeof value.supports_cluster_selection === 'boolean' ? value.supports_cluster_selection : null,
    supports_related_lookup:
      typeof value.supports_related_lookup === 'boolean' ? value.supports_related_lookup : null,
    supports_compare_mode: typeof value.supports_compare_mode === 'boolean' ? value.supports_compare_mode : null,
    supports_summary_card:
      typeof value.supports_summary_card === 'boolean' ? value.supports_summary_card : null,
  };
};

const normalizeInteractivitySourceDefinition = (
  value: unknown,
): MapExperienceInteractivitySourceDefinition | null => {
  if (!isRecord(value)) return null;
  const sourceId = ensureString(value.source_id).trim();
  const sourceLayer = ensureString(value.source_layer).trim();
  const identity = isRecord(value.identity) ? value.identity : null;
  const featureIdField = ensureString(identity?.feature_id_field).trim();
  if (!sourceId || !sourceLayer || !featureIdField) return null;
  return {
    source_id: sourceId,
    source_layer: sourceLayer,
    label: ensureString(value.label).trim() || null,
    description: ensureString(value.description).trim() || null,
    geometry_kind: normalizeInteractivityGeometryKind(value.geometry_kind),
    identity: {
      feature_id_field: featureIdField,
    },
    fields: toArray(value.fields)
      .map(normalizeInteractivityFieldDefinition)
      .filter((entry): entry is MapExperienceInteractivityFieldDefinition => Boolean(entry)),
    capabilities: normalizeInteractivitySourceCapabilities(value.capabilities),
    semantic_roles: isRecord(value.semantic_roles) ? normalizeStringRecord(value.semantic_roles) : null,
    relation_hints: toArray<Record<string, unknown>>(value.relation_hints)
      .map(entry => (isRecord(entry) ? cloneJsonValue(entry) : null))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry)),
    resolver_ids: normalizeStringArray(value.resolver_ids),
    surface_ids: normalizeStringArray(value.surface_ids),
  };
};

const normalizeBuiltinValueDefinition = (value: unknown): MapExperienceBuiltinValueDefinition | null => {
  if (!isRecord(value)) return null;
  const valueId = normalizeBuiltinValueId(value.value_id);
  const label = ensureString(value.label).trim();
  const valueType = normalizeInteractivityValueType(value.value_type);
  if (!valueId || !label || !valueType) return null;
  return {
    value_id: valueId,
    label,
    value_type: valueType,
    description: ensureString(value.description).trim() || null,
    requires_capability: ensureString(value.requires_capability).trim() || null,
  };
};

const normalizeResolverDefinition = (value: unknown): MapExperienceResolverDefinition | null => {
  if (!isRecord(value)) return null;
  const resolverId = ensureString(value.resolver_id).trim();
  if (!resolverId) return null;
  return {
    resolver_id: resolverId,
    resolver_attachment_id: ensureString(value.resolver_attachment_id).trim() || null,
    resolver_type_id: ensureString(value.resolver_type_id).trim() || null,
    resolver_connection_id: ensureString(value.resolver_connection_id).trim() || null,
    source_id: ensureString(value.source_id).trim() || null,
    source_layer: ensureString(value.source_layer).trim() || null,
    label: ensureString(value.label).trim() || null,
    description: ensureString(value.description).trim() || null,
    provider: ensureString(value.provider).trim() || null,
    availability: normalizeResolverAvailability(value.availability),
    availability_reason: ensureString(value.availability_reason).trim() || null,
    connection_status: normalizeResolverConnectionStatus(value.connection_status),
    input_schema: isRecord(value.input_schema) ? cloneJsonValue(value.input_schema) : null,
    output_schema: isRecord(value.output_schema) ? cloneJsonValue(value.output_schema) : null,
    timeout_ms: isFiniteNumber(value.timeout_ms) ? value.timeout_ms : null,
    cache_ttl_ms: isFiniteNumber(value.cache_ttl_ms) ? value.cache_ttl_ms : null,
    permissions: normalizeStringArray(value.permissions),
    required_permissions: normalizeStringArray(value.required_permissions),
    required_capabilities: normalizeStringArray(value.required_capabilities),
    failure_policy: normalizeResolverFailurePolicy(value.failure_policy),
    result_limit: isFiniteNumber(value.result_limit) ? value.result_limit : null,
    supported_renderer_kinds: toArray(value.supported_renderer_kinds)
      .map(normalizeResolverRendererKind)
      .filter((entry): entry is MapExperienceResolverRendererKind => Boolean(entry)),
    default_renderer_kind: normalizeResolverRendererKind(value.default_renderer_kind),
    renderer_kind: normalizeResolverRendererKind(value.renderer_kind),
    required_inputs: toArray<Record<string, unknown>>(value.required_inputs)
      .map(entry => {
        if (!isRecord(entry)) return null;
        const inputKey = ensureString(entry.input_key).trim();
        if (!inputKey) return null;
        return {
          input_key: inputKey,
          label: ensureString(entry.label).trim() || null,
          description: ensureString(entry.description).trim() || null,
          binding_kind: normalizeResolverInputBindingKind(entry.binding_kind),
          binding: isRecord(entry.binding) ? cloneJsonValue(entry.binding) : null,
          required: typeof entry.required === 'boolean' ? entry.required : null,
        };
      })
      .filter(
        (
          entry,
        ): entry is NonNullable<MapExperienceResolverDefinition['required_inputs']>[number] => Boolean(entry),
      ),
    safe_overrides: isRecord(value.safe_overrides) ? cloneJsonValue(value.safe_overrides) : null,
  };
};

const normalizeSurfaceBlockDefinition = (value: unknown): MapExperienceSurfaceBlockDefinition | null => {
  if (!isRecord(value)) return null;
  const kind = normalizeSurfaceBlockKind(value.kind);
  if (!kind) return null;
  return {
    id: ensureString(value.id).trim() || null,
    kind,
    label: ensureString(value.label).trim() || null,
    binding: isRecord(value.binding) ? cloneJsonValue(value.binding) : null,
    items: toArray<Record<string, unknown>>(value.items)
      .map(entry => (isRecord(entry) ? cloneJsonValue(entry) : null))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry)),
    empty_state: ensureString(value.empty_state).trim() || null,
  };
};

const normalizeSurfaceTabDefinition = (value: unknown): MapExperienceSurfaceTabDefinition | null => {
  if (!isRecord(value)) return null;
  const tabId = ensureString(value.tab_id).trim();
  const label = ensureString(value.label).trim();
  if (!tabId || !label) return null;
  return {
    tab_id: tabId,
    label,
    sections: toArray(value.sections)
      .map(normalizeSurfaceBlockDefinition)
      .filter((entry): entry is MapExperienceSurfaceBlockDefinition => Boolean(entry)),
  };
};

const normalizeSurfaceDefinition = (value: unknown): MapExperienceSurfaceDefinition | null => {
  if (!isRecord(value)) return null;
  const surfaceId = ensureString(value.surface_id).trim();
  const surfaceKind = normalizeSurfaceKind(value.surface_kind);
  if (!surfaceId || !surfaceKind) return null;
  return {
    surface_id: surfaceId,
    source_id: ensureString(value.source_id).trim() || null,
    source_layer: ensureString(value.source_layer).trim() || null,
    surface_kind: surfaceKind,
    label: ensureString(value.label).trim() || null,
    title_binding: isRecord(value.title_binding) ? cloneJsonValue(value.title_binding) : null,
    data_binding: isRecord(value.data_binding) ? cloneJsonValue(value.data_binding) : null,
    sections: toArray(value.sections)
      .map(normalizeSurfaceBlockDefinition)
      .filter((entry): entry is MapExperienceSurfaceBlockDefinition => Boolean(entry)),
    tabs: toArray(value.tabs)
      .map(normalizeSurfaceTabDefinition)
      .filter((entry): entry is MapExperienceSurfaceTabDefinition => Boolean(entry)),
  };
};

const normalizeTargetSelectorDefinition = (value: unknown): MapExperienceTargetSelectorDefinition | null => {
  if (!isRecord(value)) return null;
  const operator = normalizeTargetOperator(value.operator);
  if (!operator) return null;
  const target = isRecord(value.target)
    ? {
        source_id: ensureString(value.target.source_id).trim() || null,
        source_layer: ensureString(value.target.source_layer).trim() || null,
      }
    : null;
  const relation = isRecord(value.relation)
    ? {
        left_field: ensureString(value.relation.left_field).trim() || null,
        right_field: ensureString(value.relation.right_field).trim() || null,
      }
    : null;
  return {
    selector_id: ensureString(value.selector_id).trim() || null,
    operator,
    mode: normalizeSpatialQueryMode(value.mode),
    reference: isRecord(value.reference) ? cloneJsonValue(value.reference) : null,
    target,
    relation,
    radius_m: isFiniteNumber(value.radius_m) ? value.radius_m : null,
    limit: isFiniteNumber(value.limit) ? value.limit : null,
    resolver_id: ensureString(value.resolver_id).trim() || null,
  };
};

const normalizeConditionGroupDefinition = (
  value: unknown,
  depth = 0,
): MapExperienceConditionGroupDefinition | null => {
  if (depth > 4 || !isRecord(value)) return null;
  const maybeRules = Array.isArray(value.rules) ? value.rules : null;
  if (!maybeRules) return null;
  const rules = maybeRules
    .map(entry => {
      const nested = normalizeConditionGroupDefinition(entry, depth + 1);
      if (nested) return nested;
      if (!isRecord(entry)) return null;
      const operator = normalizeConditionOperator(entry.operator);
      if (!operator) return null;
      return {
        field_ref: isRecord(entry.field_ref) ? cloneJsonValue(entry.field_ref) : null,
        operator,
        value: typeof entry.value === 'undefined' ? null : cloneJsonValue(entry.value),
        reference: isRecord(entry.reference) ? cloneJsonValue(entry.reference) : null,
        radius_m: isFiniteNumber(entry.radius_m) ? entry.radius_m : null,
      } satisfies MapExperienceConditionRuleDefinition;
    })
    .filter(
      (entry): entry is MapExperienceConditionGroupDefinition | MapExperienceConditionRuleDefinition => Boolean(entry),
    );
  if (rules.length === 0) return null;
  return {
    operator: normalizeConditionGroupOperator(value.operator),
    rules,
  };
};

const normalizePipelineStepDefinition = (value: unknown): MapExperiencePipelineStepDefinition | null => {
  if (!isRecord(value)) return null;
  const stepId = ensureString(value.step_id).trim();
  const kind = ensureString(value.kind).trim() as MapExperiencePipelineStepKind;
  if (!stepId) return null;
  if (kind === 'resolve') {
    const resolverId = ensureString(value.resolver_id).trim();
    if (!resolverId) return null;
    return {
      step_id: stepId,
      kind,
      resolver_id: resolverId,
      resolver_attachment_id: ensureString(value.resolver_attachment_id).trim() || null,
      resolver_type_id: ensureString(value.resolver_type_id).trim() || null,
      input: isRecord(value.input) ? cloneJsonValue(value.input) : null,
      input_mappings: toArray<Record<string, unknown>>(value.input_mappings)
        .map(entry => {
          if (!isRecord(entry)) return null;
          const inputKey = ensureString(entry.input_key).trim();
          const bindingKind = normalizeResolverInputBindingKind(entry.binding_kind);
          if (!inputKey || !bindingKind) return null;
          return {
            input_key: inputKey,
            binding_kind: bindingKind,
            binding: isRecord(entry.binding) ? cloneJsonValue(entry.binding) : null,
            required: typeof entry.required === 'boolean' ? entry.required : null,
          };
        })
        .filter(
          (
            entry,
          ): entry is NonNullable<MapExperiencePipelineResolveStepDefinition['input_mappings']>[number] =>
            Boolean(entry),
        ),
      failure_policy: normalizeResolverFailurePolicy(value.failure_policy),
    };
  }
  if (kind === 'target_set') {
    const selector = normalizeTargetSelectorDefinition(value.selector);
    if (!selector) return null;
    return {
      step_id: stepId,
      kind,
      input: ensureString(value.input).trim() || null,
      selector,
    };
  }
  if (kind === 'condition') {
    const logic = normalizeConditionGroupDefinition(value.logic);
    if (!logic) return null;
    return {
      step_id: stepId,
      kind,
      input: ensureString(value.input).trim() || null,
      target: normalizeTargetSelectorDefinition(value.target),
      logic,
    };
  }
  return null;
};

const normalizeEffectDefinition = (value: unknown): MapExperienceEffectDefinition | null => {
  if (!isRecord(value)) return null;
  const kind = normalizeEffectKind(value.kind);
  if (!kind) return null;
  const input =
    typeof value.input === 'string'
      ? value.input.trim() || null
      : isRecord(value.input)
        ? cloneJsonValue(value.input)
        : null;
  return {
    effect_id: ensureString(value.effect_id).trim() || null,
    kind,
    input,
    surface_id: ensureString(value.surface_id).trim() || null,
    tool_id: normalizeBuiltinToolId(value.tool_id),
    style: ensureString(value.style).trim() || null,
    filter_id: ensureString(value.filter_id).trim() || null,
    value: typeof value.value === 'undefined' ? null : cloneJsonValue(value.value),
    auto_clear_ms: isFiniteNumber(value.auto_clear_ms) ? value.auto_clear_ms : null,
  };
};

const normalizeToolRailDefinition = (value: unknown): MapExperienceToolRailDefinition | null => {
  if (!isRecord(value)) return null;
  return {
    default_tool_id: normalizeBuiltinToolId(value.default_tool_id),
    visible_tool_ids: toArray(value.visible_tool_ids)
      .map(normalizeBuiltinToolId)
      .filter((entry): entry is MapExperienceBuiltinToolId => Boolean(entry)),
  };
};

const normalizePipelineDefinition = (value: unknown): MapExperiencePipelineDefinition | null => {
  if (!isRecord(value)) return null;
  const pipelineId = ensureString(value.pipeline_id).trim();
  const trigger = isRecord(value.trigger) ? value.trigger : null;
  const triggerKind = normalizePipelineTriggerKind(trigger?.kind);
  if (!pipelineId || !triggerKind) return null;
  return {
    pipeline_id: pipelineId,
    source_id: ensureString(value.source_id).trim() || null,
    source_layer: ensureString(value.source_layer).trim() || null,
    label: ensureString(value.label).trim() || null,
    trigger: {
      kind: triggerKind,
      source: ensureString(trigger?.source).trim() || null,
      geometry_kind:
        trigger?.geometry_kind === 'point' || trigger?.geometry_kind === 'line' || trigger?.geometry_kind === 'polygon'
          ? trigger.geometry_kind
          : null,
    },
    steps: toArray(value.steps)
      .map(normalizePipelineStepDefinition)
      .filter((entry): entry is MapExperiencePipelineStepDefinition => Boolean(entry)),
    effects: toArray(value.effects)
      .map(normalizeEffectDefinition)
      .filter((entry): entry is MapExperienceEffectDefinition => Boolean(entry)),
    stop_propagation: typeof value.stop_propagation === 'boolean' ? value.stop_propagation : null,
  };
};

export const createDefaultMapExperienceBuiltinValues = (): MapExperienceBuiltinValueDefinition[] => [
  { value_id: 'my_location', label: 'My Location', value_type: 'geometry', requires_capability: 'geolocation' },
  { value_id: 'selected_feature', label: 'Selected Feature', value_type: 'feature_ref' },
  { value_id: 'hovered_feature', label: 'Hovered Feature', value_type: 'feature_ref' },
  { value_id: 'trigger_feature', label: 'Trigger Feature', value_type: 'feature_ref' },
  { value_id: 'map_center', label: 'Map Center', value_type: 'geometry' },
  { value_id: 'viewport_bounds', label: 'Viewport Bounds', value_type: 'geometry' },
  { value_id: 'active_preset', label: 'Active Preset', value_type: 'string' },
  { value_id: 'active_filter_values', label: 'Active Filter Values', value_type: 'string' },
  { value_id: 'current_time', label: 'Current Time', value_type: 'date' },
  { value_id: 'current_user', label: 'Current User', value_type: 'string' },
  { value_id: 'current_account', label: 'Current Account', value_type: 'string' },
  { value_id: 'current_tenant', label: 'Current Tenant', value_type: 'string' },
];

const normalizeInteractivityDefinition = (value: unknown): MapExperienceInteractivityDefinition | null => {
  if (!isRecord(value)) return null;
  return {
    sources: toArray(value.sources)
      .map(normalizeInteractivitySourceDefinition)
      .filter((entry): entry is MapExperienceInteractivitySourceDefinition => Boolean(entry)),
    builtin_values: (() => {
      const builtinValues = toArray(value.builtin_values)
        .map(normalizeBuiltinValueDefinition)
        .filter((entry): entry is MapExperienceBuiltinValueDefinition => Boolean(entry));
      return builtinValues.length > 0 ? builtinValues : createDefaultMapExperienceBuiltinValues();
    })(),
    resolvers: toArray(value.resolvers)
      .map(normalizeResolverDefinition)
      .filter((entry): entry is MapExperienceResolverDefinition => Boolean(entry)),
    surfaces: toArray(value.surfaces)
      .map(normalizeSurfaceDefinition)
      .filter((entry): entry is MapExperienceSurfaceDefinition => Boolean(entry)),
    pipelines: toArray(value.pipelines)
      .map(normalizePipelineDefinition)
      .filter((entry): entry is MapExperiencePipelineDefinition => Boolean(entry)),
  };
};

export const createEmptyMapExperienceMetadata = (): MapExperienceMetadataV1 => ({
  version: 1,
  style_profiles: [],
  active_style_profiles: {},
  render_stacks: [],
  filter_sets: [],
  active_filter_values: {},
  presets: [],
  selected_preset_id: null,
  tools: null,
  interactivity: null,
});

export const loadMapExperienceMetadata = (value: unknown): MapExperienceMetadataV1 => {
  if (!isRecord(value) || (value.version !== 1 && value.version !== 2)) {
    return createEmptyMapExperienceMetadata();
  }
  return {
    version: value.version === 2 ? 2 : 1,
    style_profiles: toArray(value.style_profiles).map(normalizeStyleProfile).filter((entry): entry is MapExperienceStyleProfile => Boolean(entry)),
    active_style_profiles: normalizeStringRecord(value.active_style_profiles),
    render_stacks: toArray(value.render_stacks).map(normalizeRenderStack).filter((entry): entry is MapExperienceRenderStackDefinition => Boolean(entry)),
    filter_sets: toArray(value.filter_sets).map(normalizeFilterSet).filter((entry): entry is MapExperienceFilterSet => Boolean(entry)),
    active_filter_values: normalizeActiveFilterValues(value.active_filter_values),
    presets: toArray(value.presets).map(normalizePreset).filter((entry): entry is MapExperiencePresetDefinition => Boolean(entry)),
    selected_preset_id: ensureString(value.selected_preset_id).trim() || null,
    tools: normalizeToolRailDefinition(value.tools),
    interactivity: normalizeInteractivityDefinition(value.interactivity),
  };
};

export const serializeMapExperienceMetadata = (value: MapExperienceMetadataV1): MapExperienceMetadataV1 => {
  const normalized = loadMapExperienceMetadata(value);
  if (normalized.interactivity || normalized.tools || (normalized.render_stacks?.length ?? 0) > 0) {
    return {
      ...normalized,
      version: 2,
    };
  }
  return normalized;
};

const propNamesForKind = (kind: string) => {
  switch (kind) {
    case 'fill':
      return { color: ['fill-color'], opacity: ['fill-opacity'] };
    case 'line':
      return { color: ['line-color'], opacity: ['line-opacity'] };
    case 'fill-extrusion':
      return { color: ['fill-extrusion-color'], opacity: ['fill-extrusion-opacity'] };
    case 'circle':
      return { color: ['circle-color'], opacity: ['circle-opacity'] };
    case 'symbol':
      return { color: ['text-color', 'icon-color'], opacity: ['text-opacity', 'icon-opacity'] };
    default:
      return { color: [] as string[], opacity: [] as string[] };
  }
};

const ensureExperienceBaseline = (layer: Record<string, unknown>, kind: string, meta: WorkbenchMeta): ExperienceBaseline => {
  const existing = meta.experience_baseline;
  if (existing && typeof existing === 'object') {
    return existing;
  }

  const baseline: ExperienceBaseline = {
    filter: (layer as any).filter ?? null,
    paint: { defined: {}, values: {} },
  };
  const paint = isRecord((layer as any).paint) ? ((layer as any).paint as Record<string, unknown>) : null;
  const props = propNamesForKind(kind);
  [...props.color, ...props.opacity].forEach(prop => {
    baseline.paint.defined[prop] = Boolean(paint && Object.prototype.hasOwnProperty.call(paint, prop));
    baseline.paint.values[prop] = paint ? paint[prop] : undefined;
  });
  meta.experience_baseline = baseline;
  return baseline;
};

const restoreMaybeDeletePaintProp = (paint: Record<string, unknown>, baseline: ExperienceBaseline, prop: string) => {
  if (baseline.paint.defined[prop]) {
    paint[prop] = baseline.paint.values[prop];
  } else {
    delete paint[prop];
  }
};

const applyCombinedFilter = (layerRecord: Record<string, unknown>, baseline: ExperienceBaseline, extra: Expr | null) => {
  if (!extra) {
    if (baseline.filter === null) {
      delete layerRecord.filter;
    } else {
      layerRecord.filter = baseline.filter;
    }
    return;
  }
  if (Array.isArray(baseline.filter)) {
    layerRecord.filter = ['all', baseline.filter, extra];
    return;
  }
  layerRecord.filter = extra;
};

const buildSingleColorExpression = (
  profile: MapExperienceSingleStyleProfile,
  themeColors?: MapExperienceThemeColorPalette | null,
  colorMode: MapExperienceCompileColorMode = 'literal',
  globalStateDefaults: Map<MapExperienceSemanticColorToken, string> = new Map(),
): Expr => resolveCompiledMapExperienceColorValue(profile.color, themeColors, colorMode, globalStateDefaults);

const buildCategoricalColorExpression = (
  profile: MapExperienceCategoricalStyleProfile,
  themeColors?: MapExperienceThemeColorPalette | null,
  colorMode: MapExperienceCompileColorMode = 'literal',
  globalStateDefaults: Map<MapExperienceSemanticColorToken, string> = new Map(),
): Expr | null => {
  if (!profile.field || profile.categories.length === 0) return null;
  return [
    'match',
    ['to-string', ['get', profile.field]],
    ...profile.categories.flatMap(category => [
      category.value,
      resolveCompiledMapExperienceColorValue(category.color, themeColors, colorMode, globalStateDefaults),
    ]),
    resolveCompiledMapExperienceColorValue(profile.fallback_color, themeColors, colorMode, globalStateDefaults),
  ];
};

const buildRangePredicate = (field: string, item: MapExperienceRangeBreak): Expr | null => {
  const accessor: Expr = ['get', field];
  if (isFiniteNumber(item.min) && isFiniteNumber(item.max)) {
    return ['all', ['>=', accessor, item.min], ['<', accessor, item.max]];
  }
  if (isFiniteNumber(item.min)) {
    return ['>=', accessor, item.min];
  }
  if (isFiniteNumber(item.max)) {
    return ['<', accessor, item.max];
  }
  return null;
};

const buildRangeColorExpression = (
  profile: MapExperienceRangeStyleProfile,
  themeColors?: MapExperienceThemeColorPalette | null,
  colorMode: MapExperienceCompileColorMode = 'literal',
  globalStateDefaults: Map<MapExperienceSemanticColorToken, string> = new Map(),
): Expr | null => {
  if (!profile.field || profile.breaks.length === 0) return null;
  const parts: Expr[] = ['case'];
  profile.breaks.forEach(item => {
    const predicate = buildRangePredicate(profile.field, item);
    if (!predicate) return;
    parts.push(predicate, resolveCompiledMapExperienceColorValue(item.color, themeColors, colorMode, globalStateDefaults));
  });
  parts.push(resolveCompiledMapExperienceColorValue(profile.fallback_color, themeColors, colorMode, globalStateDefaults));
  return parts.length > 3 ? parts : null;
};

export const hasConfiguredMapExperienceFilterValue = (kind: MapExperienceFilterKind, value: unknown): boolean => {
  switch (kind) {
    case 'boolean':
      return typeof value === 'boolean';
    case 'single_select':
    case 'text_contains':
      return typeof value === 'string' && value.trim().length > 0;
    case 'multi_select':
      return Array.isArray(value) && value.some(entry => typeof entry === 'string' && entry.trim().length > 0);
    case 'numeric_range':
      return (
        isRecord(value) &&
        !(isFiniteNumber(value.min) && isFiniteNumber(value.max) && value.min > value.max) &&
        [value.min, value.max].some(entry => typeof entry === 'number' ? Number.isFinite(entry) : entry != null)
      );
    case 'date_range':
      return (
        isRecord(value) &&
        [value.min, value.max].some(entry => typeof entry === 'string' && entry.trim().length > 0)
      );
    default:
      return false;
  }
};

export const resolveMapExperienceFilterValue = (definition: MapExperienceFilterDefinition, value: unknown): unknown => {
  if (value !== undefined) {
    return normalizeFilterRuntimeValue(definition.kind, value);
  }
  return normalizeFilterRuntimeValue(definition.kind, definition.default_value);
};

export const resolveMapExperienceServerBindingKey = (definition: Pick<MapExperienceFilterDefinition, 'id' | 'server_binding_key'>) =>
  (typeof definition.server_binding_key === 'string' && definition.server_binding_key.trim()) || definition.id;

export interface MapExperienceServerManagedFilterRef {
  filterSetId: string;
  filterId: string;
  sourceId: string;
  sourceLayer: string;
  serverBindingKey: string;
  serverExecution: MapExperienceServerExecution;
}

const shouldServerManageFilter = (
  definition: MapExperienceFilterDefinition,
  availableBindingKeys: Set<string>,
): boolean => {
  const execution = definition.server_execution ?? 'auto';
  if (execution === 'client_only') {
    return false;
  }
  const bindingKey = resolveMapExperienceServerBindingKey(definition);
  if (execution === 'server_only') {
    return true;
  }
  return bindingKey !== '' && availableBindingKeys.has(bindingKey);
};

export const collectMapExperienceServerManagedFilters = (
  experience: MapExperienceMetadataV1,
  args: {
    sourceId: string;
    sourceAlias?: string | null;
    sourceLayer?: string | null;
    availableBindingKeys?: Iterable<string> | null;
  },
): MapExperienceServerManagedFilterRef[] => {
  const sourceId = args.sourceId.trim();
  const sourceAlias = args.sourceAlias?.trim() ?? '';
  const sourceLayer = args.sourceLayer?.trim() ?? '';
  const availableBindingKeys = new Set(Array.from(args.availableBindingKeys ?? []).map(value => `${value}`.trim()).filter(Boolean));
  if (!sourceId) {
    return [];
  }
  return experience.filter_sets.flatMap(filterSet => {
    if (!matchesExperienceSourceId(filterSet.source_id, sourceId, sourceAlias)) {
      return [];
    }
    if (sourceLayer && filterSet.source_layer !== sourceLayer) {
      return [];
    }
    return filterSet.filters
      .filter(definition => shouldServerManageFilter(definition, availableBindingKeys))
      .map(definition => ({
        filterSetId: filterSet.id,
        filterId: definition.id,
        sourceId: filterSet.source_id,
        sourceLayer: filterSet.source_layer,
        serverBindingKey: resolveMapExperienceServerBindingKey(definition),
        serverExecution: definition.server_execution ?? 'auto',
      }));
  });
};

export const omitMapExperienceFilterValues = (
  experience: MapExperienceMetadataV1,
  removals: MapExperienceServerManagedFilterRef[],
): MapExperienceMetadataV1 => {
  if (!removals.length) {
    return experience;
  }
  const byFilterSet = new Map<string, Set<string>>();
  removals.forEach(removal => {
    if (!byFilterSet.has(removal.filterSetId)) {
      byFilterSet.set(removal.filterSetId, new Set());
    }
    byFilterSet.get(removal.filterSetId)!.add(removal.filterId);
  });
  return {
    ...experience,
    active_filter_values: Object.fromEntries(
      Object.entries(experience.active_filter_values).map(([filterSetId, values]) => {
        const removedIds = byFilterSet.get(filterSetId);
        if (!removedIds || removedIds.size === 0) {
          return [filterSetId, values];
        }
        return [
          filterSetId,
          Object.fromEntries(
            Object.entries(values).filter(([filterId]) => !removedIds.has(filterId)),
          ),
        ];
      }),
    ),
  };
};

export interface MapExperienceGeoJSONQueryRequest {
  filter_sets?: Array<Record<string, unknown>>;
  active_filter_values?: Record<string, Record<string, unknown>>;
  context?: Record<string, unknown>;
}

export const buildMapExperienceGeoJSONQueryRequest = (
  experience: MapExperienceMetadataV1,
  args: {
    sourceId: string;
    sourceAlias?: string | null;
    sourceLayer?: string | null;
    context?: Record<string, unknown> | null;
  },
): MapExperienceGeoJSONQueryRequest | null => {
  const sourceId = args.sourceId.trim();
  const sourceAlias = args.sourceAlias?.trim() ?? '';
  const sourceLayer = args.sourceLayer?.trim() ?? '';
  if (!sourceId) {
    return null;
  }

  const filterSets = experience.filter_sets
    .filter(
      filterSet =>
        matchesExperienceSourceId(filterSet.source_id, sourceId, sourceAlias) &&
        (!sourceLayer || filterSet.source_layer === sourceLayer),
    )
    .map(filterSet => ({
      id: filterSet.id,
      source_id: filterSet.source_id,
      source_layer: filterSet.source_layer,
      filters: filterSet.filters.map(filter => ({
        id: filter.id,
        kind: filter.kind,
        field: filter.field,
        operator: filter.operator ?? null,
        value: filter.value ?? null,
        server_binding_key: resolveMapExperienceServerBindingKey(filter),
        server_execution: filter.server_execution ?? 'auto',
      })),
    }));

  if (filterSets.length === 0 && !args.context) {
    return null;
  }

  const activeFilterValues = Object.fromEntries(
    filterSets.map(filterSet => [filterSet.id as string, cloneJsonValue(experience.active_filter_values[filterSet.id as string] ?? {})]),
  );

  return {
    filter_sets: filterSets,
    active_filter_values: activeFilterValues,
    context: args.context ? cloneJsonValue(args.context) : undefined,
  };
};

const buildFilterExpression = (definition: MapExperienceFilterDefinition, value: unknown): Expr | null => {
  const resolvedValue = resolveMapExperienceFilterValue(definition, value);
  const accessor: Expr = ['get', definition.field];
  const stringAccessor: Expr = ['to-string', accessor];
  switch (definition.kind) {
    case 'boolean': {
      if (typeof resolvedValue !== 'boolean' || !resolvedValue) return null;
      const operator = definition.operator ?? 'equals';
      switch (operator) {
        case 'exists':
          return ['has', definition.field];
        case 'not_exists':
          return ['!', ['has', definition.field]];
        case 'equals':
          if (definition.value == null) return null;
          if (typeof definition.value === 'number' && Number.isFinite(definition.value)) {
            return ['==', ['to-number', accessor, definition.value + 1], definition.value];
          }
          if (typeof definition.value === 'boolean') {
            return ['==', ['downcase', stringAccessor], String(definition.value)];
          }
          return ['==', stringAccessor, String(definition.value)];
        case 'not_equals':
          if (definition.value == null) return null;
          if (typeof definition.value === 'number' && Number.isFinite(definition.value)) {
            return ['!=', ['to-number', accessor, definition.value], definition.value];
          }
          if (typeof definition.value === 'boolean') {
            return ['!=', ['downcase', stringAccessor], String(definition.value)];
          }
          return ['!=', stringAccessor, String(definition.value)];
        case 'greater_than':
          return typeof definition.value === 'number' && Number.isFinite(definition.value)
            ? ['>', ['to-number', accessor, definition.value], definition.value]
            : null;
        case 'less_than':
          return typeof definition.value === 'number' && Number.isFinite(definition.value)
            ? ['<', ['to-number', accessor, definition.value], definition.value]
            : null;
        default:
          return null;
      }
    }
    case 'single_select':
      if (typeof resolvedValue !== 'string' || !resolvedValue.trim()) return null;
      return ['==', stringAccessor, resolvedValue];
    case 'multi_select': {
      if (!Array.isArray(resolvedValue)) return null;
      const values = Array.from(new Set(resolvedValue.map(entry => String(entry).trim()).filter(Boolean)));
      if (values.length === 0) return null;
      return ['match', stringAccessor, values, true, false];
    }
    case 'numeric_range': {
      if (!isRecord(resolvedValue)) return null;
      const min = resolvedValue.min == null ? null : Number(resolvedValue.min);
      const max = resolvedValue.max == null ? null : Number(resolvedValue.max);
      if (Number.isFinite(min) && Number.isFinite(max) && min > max) return null;
      const clauses: Expr[] = [];
      clauses.push(['has', definition.field]);
      if (Number.isFinite(min)) clauses.push(['>=', ['to-number', accessor, min - 1], min]);
      if (Number.isFinite(max)) clauses.push(['<=', ['to-number', accessor, max + 1], max]);
      if (clauses.length <= 1) return null;
      return clauses.length === 1 ? clauses[0] : ['all', ...clauses];
    }
    case 'text_contains':
      if (typeof resolvedValue !== 'string' || !resolvedValue.trim()) return null;
      return ['in', resolvedValue.toLowerCase(), ['downcase', stringAccessor]];
    case 'date_range':
      if (!isRecord(resolvedValue)) return null;
      {
        const min = typeof resolvedValue.min === 'string' && resolvedValue.min.trim() ? resolvedValue.min.trim() : null;
        const max = typeof resolvedValue.max === 'string' && resolvedValue.max.trim() ? resolvedValue.max.trim() : null;
        const clauses: Expr[] = [];
        if (min) clauses.push(['>=', stringAccessor, min]);
        if (max) clauses.push(['<=', stringAccessor, max]);
        if (clauses.length === 0) return null;
        return clauses.length === 1 ? clauses[0] : ['all', ...clauses];
      }
    default:
      return null;
  }
};

const buildTargetFilterExpression = (
  targetKeys: TargetKey[],
  experience: MapExperienceMetadataV1,
): Expr | null => {
  const targetKeySet = new Set(targetKeys);
  const sets = experience.filter_sets.filter(set => {
    const setSourceId = typeof set.source_id === 'string' ? set.source_id.trim() : '';
    const setSourceLayer = typeof set.source_layer === 'string' ? set.source_layer.trim() : '';
    if (!setSourceId || !setSourceLayer) {
      return false;
    }
    return targetKeySet.has(buildTargetKey(setSourceId, setSourceLayer));
  });
  const predicates = sets.flatMap(set => {
    const activeValues = experience.active_filter_values[set.id] ?? {};
    return set.filters
      .map(filter => buildFilterExpression(filter, activeValues[filter.id]))
      .filter((entry): entry is Expr => Boolean(entry));
  });
  if (predicates.length === 0) return null;
  return predicates.length === 1 ? predicates[0] : ['all', ...predicates];
};

export const buildMapExperienceTargetFilterExpression = (
  experience: MapExperienceMetadataV1,
  sourceId: string,
  sourceLayer: string,
  sourceAlias?: string | null,
): Expr | null => buildTargetFilterExpression(buildTargetKeys(sourceId, sourceLayer, sourceAlias), experience);

const slugifyRenderStackIdPart = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'entry';

const parseRenderStackBoolean = (value: string): boolean | null => {
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return null;
};

const coerceRenderStackLiteral = (raw: string): unknown => {
  const bool = parseRenderStackBoolean(raw);
  if (bool != null) {
    return bool;
  }
  const num = Number(raw);
  if (Number.isFinite(num) && raw.trim() !== '') {
    return num;
  }
  return raw;
};

const buildRenderStackCondition = (logic: Extract<StyleRuleLogic, { type: 'condition' }>): Expr | null => {
  const op = logic.operatorId.trim().toLowerCase();
  if (op === 'match_everything') {
    return ['all'];
  }
  if (!logic.fieldId) {
    return null;
  }
  const accessor: Expr = ['get', logic.fieldId];
  const rawValues = Array.isArray(logic.values) ? logic.values.map(String) : [];
  const firstRaw = rawValues[0] ?? '';
  const first = coerceRenderStackLiteral(firstRaw);

  switch (op) {
    case 'eq':
    case 'equals':
      return ['==', accessor, first];
    case 'neq':
    case 'not_equals':
      return ['!=', accessor, first];
    case 'gt':
      return ['>', accessor, coerceRenderStackLiteral(firstRaw)];
    case 'gte':
      return ['>=', accessor, coerceRenderStackLiteral(firstRaw)];
    case 'lt':
      return ['<', accessor, coerceRenderStackLiteral(firstRaw)];
    case 'lte':
      return ['<=', accessor, coerceRenderStackLiteral(firstRaw)];
    case 'contains':
      return ['in', firstRaw, ['to-string', accessor]];
    case 'in':
      return ['match', accessor, ['literal', rawValues.map(coerceRenderStackLiteral)], true, false];
    case 'exists':
      return ['has', logic.fieldId];
    default:
      return ['==', accessor, first];
  }
};

const buildRenderStackFilterExpression = (logic: StyleRuleLogic | null | undefined): Expr | null => {
  if (!logic) return null;
  if (logic.type === 'condition') {
    return buildRenderStackCondition(logic);
  }
  const compiled = logic.children
    .map(child => buildRenderStackFilterExpression(child))
    .filter((entry): entry is Expr => Boolean(entry));
  if (compiled.length === 0) return null;
  if (compiled.length === 1) return compiled[0] ?? null;
  return logic.operator === 'OR' ? ['any', ...compiled] : ['all', ...compiled];
};

const combineFilterExpressions = (...predicates: Array<Expr | null | undefined>): Expr | null => {
  const compiled = predicates.filter((entry): entry is Expr => Boolean(entry));
  if (compiled.length === 0) return null;
  if (compiled.length === 1) return compiled[0] ?? null;
  return ['all', ...compiled];
};

const applyProfilePaint = (
  paint: Record<string, unknown>,
  baseline: ExperienceBaseline,
  kind: string,
  profile: MapExperienceStyleProfile | null,
  themeColors?: MapExperienceThemeColorPalette | null,
  colorMode: MapExperienceCompileColorMode = 'literal',
  globalStateDefaults: Map<MapExperienceSemanticColorToken, string> = new Map(),
) => {
  const props = propNamesForKind(kind);
  props.color.forEach(prop => restoreMaybeDeletePaintProp(paint, baseline, prop));
  if (!profile || profile.kind === 'rules') {
    return;
  }

  const expression =
    profile.kind === 'single'
      ? buildSingleColorExpression(profile, themeColors, colorMode, globalStateDefaults)
      : profile.kind === 'categorical'
        ? buildCategoricalColorExpression(profile, themeColors, colorMode, globalStateDefaults)
        : buildRangeColorExpression(profile, themeColors, colorMode, globalStateDefaults);
  if (!expression) return;
  props.color.forEach(prop => {
    paint[prop] = expression;
  });
  if (profile.kind === 'single' && profile.paint) {
    const allowedProps =
      kind === 'fill'
        ? new Set<string>(FILL_PAINT_PROPS)
        : kind === 'line'
          ? new Set<string>(LINE_PAINT_PROPS)
          : kind === 'circle'
            ? new Set<string>(POINT_CIRCLE_PAINT_PROPS)
            : kind === 'symbol'
              ? new Set<string>(POINT_SYMBOL_PAINT_PROPS)
              : kind === 'fill-extrusion'
                ? new Set<string>(FILL_EXTRUSION_PAINT_PROPS)
                : new Set<string>();
    Object.entries(profile.paint).forEach(([prop, value]) => {
      if (!allowedProps.has(prop)) {
        return;
      }
      const normalizedPaint = normalizeMapLibrePaintOverrides({ [prop]: value });
      if (!normalizedPaint || !(prop in normalizedPaint)) {
        return;
      }
      paint[prop] = STYLE_COLOR_PROP_NAMES.has(prop)
        ? resolveCompiledMapExperienceColorValue(
            normalizeOptionalMapExperienceColorValue(normalizedPaint[prop]),
            themeColors,
            colorMode,
            globalStateDefaults,
            typeof baseline.paint.values[prop] === 'string' ? (baseline.paint.values[prop] as string) : DEFAULT_SINGLE_COLOR,
          )
        : cloneJsonValue(normalizedPaint[prop]);
    });
  }
};

const applyProfileLayout = (
  layout: Record<string, unknown>,
  compiledKind: string,
  profile: MapExperienceStyleProfile | null,
) => {
  if (!profile) {
    return;
  }
  if (profile.kind !== 'rules' && (compiledKind !== 'symbol' || profile.representation !== 'symbol')) {
    delete layout['icon-image'];
  } else if (profile.kind !== 'rules') {
    const nextIconImage = profile.icon_image ?? null;
    if (nextIconImage) {
      layout['icon-image'] = nextIconImage;
    } else {
      delete layout['icon-image'];
    }
  }
  if (profile.layout) {
    const allowedProps =
      compiledKind === 'symbol'
        ? profile.kind === 'rules'
          ? new Set<string>([
              'icon-allow-overlap',
              'icon-ignore-placement',
              'text-allow-overlap',
              'text-ignore-placement',
            ])
          : new Set<string>(POINT_SYMBOL_LAYOUT_PROPS)
        : compiledKind === 'circle'
          ? new Set<string>(POINT_CIRCLE_LAYOUT_PROPS)
          : compiledKind === 'fill'
            ? new Set<string>(FILL_LAYOUT_PROPS)
        : compiledKind === 'line'
          ? new Set<string>(LINE_LAYOUT_PROPS)
          : new Set<string>();
    Object.entries(profile.layout).forEach(([prop, value]) => {
      if (prop === 'icon-image' || !allowedProps.has(prop)) {
        return;
      }
      const normalizedLayout = normalizeMapLibreLayoutOverrides({ [prop]: value });
      if (!normalizedLayout || !(prop in normalizedLayout)) {
        return;
      }
      layout[prop] =
        prop === 'text-field'
          ? buildTextFieldTemplateExpression(normalizedLayout[prop])
          : cloneJsonValue(normalizedLayout[prop]);
    });

    if (!Object.prototype.hasOwnProperty.call(profile.layout, 'icon-overlap') && typeof layout['icon-allow-overlap'] === 'boolean') {
      layout['icon-overlap'] = layout['icon-allow-overlap'] ? 'always' : 'never';
    }
    if (!Object.prototype.hasOwnProperty.call(profile.layout, 'text-overlap') && typeof layout['text-allow-overlap'] === 'boolean') {
      layout['text-overlap'] = layout['text-allow-overlap'] ? 'always' : 'never';
    }
  }
  if (compiledKind === 'line') {
    if (typeof layout['line-cap'] !== 'string' || !layout['line-cap']) {
      layout['line-cap'] = 'butt';
    }
    if (typeof layout['line-join'] !== 'string' || !layout['line-join']) {
      layout['line-join'] = 'miter';
    }
  }
};

const applyCompiledLayerKindDefaults = (
  paint: Record<string, unknown>,
  compiledKind: string,
) => {
  if (compiledKind === 'circle') {
    const radius = typeof paint['circle-radius'] === 'number' ? paint['circle-radius'] : Number(paint['circle-radius']);
    if (!Number.isFinite(radius) || radius <= 0) {
      paint['circle-radius'] = 5;
    }
  }
};

const applyRenderStackSymbolOverlayDefaults = (
  layout: Record<string, unknown>,
  entry: ResolvedMapExperienceRenderStackEntry,
  compiledKind: string,
  options?: {
    targetHasBadgeOverlay?: boolean;
  },
) => {
  if (compiledKind !== 'symbol') {
    return;
  }
  const hasIcon =
    typeof layout['icon-image'] === 'string' && (layout['icon-image'] as string).trim().length > 0;
  const hasText =
    (typeof layout['text-field'] === 'string' && (layout['text-field'] as string).trim().length > 0)
    || Array.isArray(layout['text-field']);
  const targetHasBadgeOverlay = options?.targetHasBadgeOverlay === true;
  const shouldForceBaseOverlap = entry.stackRole === 'base' && targetHasBadgeOverlay;
  const shouldDefaultOverlayOverlap = entry.stackRole !== 'base';

  if ((shouldForceBaseOverlap || shouldDefaultOverlayOverlap) && hasIcon && typeof layout['icon-allow-overlap'] !== 'boolean') {
    layout['icon-allow-overlap'] = true;
    layout['icon-overlap'] = 'always';
  }
  if ((shouldForceBaseOverlap || shouldDefaultOverlayOverlap) && hasText && typeof layout['text-allow-overlap'] !== 'boolean') {
    layout['text-allow-overlap'] = true;
    layout['text-overlap'] = 'always';
  }
  if (entry.stackRole === 'overlay_badge') {
    if (hasIcon && typeof layout['icon-ignore-placement'] !== 'boolean') {
      layout['icon-ignore-placement'] = true;
    }
    if (hasText && typeof layout['text-ignore-placement'] !== 'boolean') {
      layout['text-ignore-placement'] = true;
    }
  }

  if (entry.stackRole === 'overlay_label') {
    const radialOffset =
      typeof layout['text-radial-offset'] === 'number'
        ? layout['text-radial-offset']
        : Number(layout['text-radial-offset']);
    const hasVariableAnchor =
      Array.isArray(layout['text-variable-anchor']) && layout['text-variable-anchor'].length > 0;
    if (Number.isFinite(radialOffset) && !hasVariableAnchor) {
      const anchor =
        typeof layout['text-anchor'] === 'string' && layout['text-anchor'].trim().length > 0
          ? layout['text-anchor'].trim()
          : 'top';
      layout['text-variable-anchor'] = [anchor];
    }
  }
};

export const POINT_CIRCLE_PAINT_PROPS = [
  'circle-color',
  'circle-radius',
  'circle-opacity',
  'circle-stroke-color',
  'circle-stroke-opacity',
  'circle-stroke-width',
  'circle-blur',
  'circle-translate',
  'circle-translate-anchor',
  'circle-pitch-scale',
  'circle-pitch-alignment',
] as const;

export const POINT_CIRCLE_LAYOUT_PROPS = [
  'circle-sort-key',
] as const;

export const POINT_SYMBOL_PAINT_PROPS = [
  'icon-color',
  'icon-opacity',
  'icon-halo-color',
  'icon-halo-width',
  'icon-halo-blur',
  'icon-translate',
  'icon-translate-anchor',
  'text-color',
  'text-opacity',
  'text-halo-color',
  'text-halo-width',
  'text-halo-blur',
  'text-translate',
  'text-translate-anchor',
] as const;

export const POINT_SYMBOL_LAYOUT_PROPS = [
  'symbol-placement',
  'symbol-spacing',
  'symbol-avoid-edges',
  'symbol-sort-key',
  'symbol-z-order',
  'icon-image',
  'icon-size',
  'icon-allow-overlap',
  'icon-overlap',
  'icon-ignore-placement',
  'icon-optional',
  'icon-rotation-alignment',
  'icon-text-fit',
  'icon-text-fit-padding',
  'icon-rotate',
  'icon-padding',
  'icon-keep-upright',
  'icon-offset',
  'icon-anchor',
  'icon-pitch-alignment',
  'text-pitch-alignment',
  'text-rotation-alignment',
  'text-field',
  'text-size',
  'text-font',
  'text-max-width',
  'text-line-height',
  'text-letter-spacing',
  'text-justify',
  'text-radial-offset',
  'text-variable-anchor',
  'text-variable-anchor-offset',
  'text-offset',
  'text-anchor',
  'text-max-angle',
  'text-writing-mode',
  'text-rotate',
  'text-padding',
  'text-keep-upright',
  'text-transform',
  'text-allow-overlap',
  'text-overlap',
  'text-ignore-placement',
  'text-optional',
] as const;

export const FILL_PAINT_PROPS = [
  'fill-antialias',
  'fill-color',
  'fill-opacity',
  'fill-outline-color',
  'fill-pattern',
  'fill-translate',
  'fill-translate-anchor',
] as const;

export const FILL_LAYOUT_PROPS = [
  'fill-sort-key',
] as const;

export const LINE_PAINT_PROPS = [
  'line-blur',
  'line-color',
  'line-dasharray',
  'line-gap-width',
  'line-gradient',
  'line-offset',
  'line-opacity',
  'line-pattern',
  'line-translate',
  'line-translate-anchor',
  'line-width',
] as const;

export const LINE_LAYOUT_PROPS = [
  'line-cap',
  'line-join',
  'line-miter-limit',
  'line-round-limit',
  'line-sort-key',
] as const;

export const FILL_EXTRUSION_PAINT_PROPS = [
  'fill-extrusion-base',
  'fill-extrusion-color',
  'fill-extrusion-height',
  'fill-extrusion-opacity',
  'fill-extrusion-pattern',
  'fill-extrusion-translate',
  'fill-extrusion-translate-anchor',
  'fill-extrusion-vertical-gradient',
] as const;

const resolveCompiledLayerKind = (
  baseKind: string,
  currentLayerType: string,
  profile: MapExperienceStyleProfile | null,
) => {
  const representation = profile?.representation ?? null;
  const pointFamily =
    baseKind === 'circle' ||
    baseKind === 'symbol' ||
    currentLayerType === 'circle' ||
    currentLayerType === 'symbol';
  if (pointFamily) {
    if (representation === 'symbol') return 'symbol';
    if (representation === 'circle') return 'circle';
    if (currentLayerType === 'symbol' || currentLayerType === 'circle') {
      return currentLayerType;
    }
    return baseKind === 'symbol' ? 'symbol' : 'circle';
  }
  const surfaceFamily =
    baseKind === 'fill' ||
    baseKind === 'line' ||
    baseKind === 'fill-extrusion' ||
    currentLayerType === 'fill' ||
    currentLayerType === 'line' ||
    currentLayerType === 'fill-extrusion';
  if (surfaceFamily) {
    if (representation === 'fill' || representation === 'line' || representation === 'fill-extrusion') {
      return representation;
    }
    if (currentLayerType === 'fill' || currentLayerType === 'line' || currentLayerType === 'fill-extrusion') {
      return currentLayerType;
    }
    if (baseKind === 'fill' || baseKind === 'line' || baseKind === 'fill-extrusion') {
      return baseKind;
    }
  }
  return currentLayerType || baseKind;
};

const normalizeCompiledLayerRepresentationState = (
  paint: Record<string, unknown>,
  layout: Record<string, unknown>,
  compiledKind: string,
) => {
  if (compiledKind === 'symbol') {
    POINT_CIRCLE_PAINT_PROPS.forEach(prop => {
      delete paint[prop];
    });
    return;
  }
  if (compiledKind === 'circle') {
    POINT_SYMBOL_PAINT_PROPS.forEach(prop => {
      delete paint[prop];
    });
    POINT_SYMBOL_LAYOUT_PROPS.forEach(prop => {
      delete layout[prop];
    });
  } else {
    POINT_CIRCLE_LAYOUT_PROPS.forEach(prop => {
      delete layout[prop];
    });
  }
  if (compiledKind !== 'fill') {
    FILL_PAINT_PROPS.forEach(prop => {
      delete paint[prop];
    });
    FILL_LAYOUT_PROPS.forEach(prop => {
      delete layout[prop];
    });
  }
  if (compiledKind !== 'line') {
    LINE_PAINT_PROPS.forEach(prop => {
      delete paint[prop];
    });
    LINE_LAYOUT_PROPS.forEach(prop => {
      delete layout[prop];
    });
  }
  if (compiledKind !== 'fill-extrusion') {
    FILL_EXTRUSION_PAINT_PROPS.forEach(prop => {
      delete paint[prop];
    });
  }
};

const buildRulesLegendGroup = (
  key: TargetKey,
  sourceId: string,
  sourceLayer: string,
  rulesLayer: LayerRuleBuilderState['layers'][number],
  themeColors?: MapExperienceThemeColorPalette | null,
): MapExperienceLegendGroup | null => {
  const resolveLegendRepresentation = (
    value: MapExperienceStyleRepresentation | LayerRuleDefinition['representation'] | null | undefined,
  ): MapExperienceStyleRepresentation | null => normalizeStyleRepresentation(value);
  const readLegendIconToken = (
    layout?: Record<string, unknown> | null,
    iconImage?: string | null,
  ): string | null => {
    const layoutIcon = layout && typeof layout['icon-image'] === 'string' ? layout['icon-image'].trim() : '';
    if (layoutIcon) {
      return layoutIcon;
    }
    const trimmedIcon = iconImage?.trim();
    return trimmedIcon || null;
  };
  const resolveLegendColor = (
    representation: MapExperienceStyleRepresentation | null,
    paint: Record<string, unknown> | null | undefined,
    fallbackColor: MapExperienceStyleColorValue | null | undefined,
  ): string => {
    if (representation && paint && isRecord(paint)) {
      const propPriority =
        representation === 'symbol'
          ? ['icon-color', 'text-color']
          : propNamesForKind(representation).color;
      for (const propName of propPriority) {
        const candidate = normalizeOptionalMapExperienceColorValue(paint[propName]);
        if (candidate) {
          return resolveMapExperienceColorValue(candidate, themeColors, DEFAULT_RULE_LEGEND_COLOR);
        }
      }
    }
    return resolveMapExperienceColorValue(fallbackColor, themeColors, DEFAULT_RULE_LEGEND_COLOR);
  };
  const entries = rulesLayer.rules
    .filter(rule => rule.enabled)
    .map(rule => {
      const representation = resolveLegendRepresentation(rule.representation);
      return {
        id: rule.id,
        label: rule.label || rule.id,
        color: resolveLegendColor(representation, rule.paint, rule.color),
        representation,
        icon_token: representation === 'symbol' ? readLegendIconToken(rule.layout) : null,
      };
    });
  if (entries.length === 0) return null;
  return {
    id: key,
    source_id: sourceId,
    source_layer: sourceLayer,
    title: sourceLayer,
    entries,
  };
};

const buildProfileLegendGroup = (
  key: TargetKey,
  profile: MapExperienceStyleProfile,
  themeColors?: MapExperienceThemeColorPalette | null,
): MapExperienceLegendGroup | null => {
  const readLegendIconToken = (
    layout?: Record<string, unknown> | null,
    iconImage?: string | null,
  ): string | null => {
    const layoutIcon = layout && typeof layout['icon-image'] === 'string' ? layout['icon-image'].trim() : '';
    if (layoutIcon) {
      return layoutIcon;
    }
    const trimmedIcon = iconImage?.trim();
    return trimmedIcon || null;
  };
  const resolveLegendColor = (
    representation: MapExperienceStyleRepresentation | null | undefined,
    paint: Record<string, unknown> | null | undefined,
    fallbackColor: MapExperienceStyleColorValue | null | undefined,
  ): string => {
    if (representation && paint && isRecord(paint)) {
      const propPriority =
        representation === 'symbol'
          ? ['icon-color', 'text-color']
          : propNamesForKind(representation).color;
      for (const propName of propPriority) {
        const candidate = normalizeOptionalMapExperienceColorValue(paint[propName]);
        if (candidate) {
          return resolveMapExperienceColorValue(candidate, themeColors, DEFAULT_RULE_LEGEND_COLOR);
        }
      }
    }
    return resolveMapExperienceColorValue(fallbackColor, themeColors, DEFAULT_RULE_LEGEND_COLOR);
  };
  switch (profile.kind) {
    case 'single':
      return {
        id: key,
        source_id: profile.source_id,
        source_layer: profile.source_layer,
        title: profile.label || profile.source_layer,
        entries: [
          {
            id: profile.id,
            label: profile.legend_label || profile.label || profile.source_layer,
            color: resolveLegendColor(profile.representation, profile.paint, profile.color),
            representation: profile.representation ?? null,
            icon_token:
              profile.representation === 'symbol'
                ? readLegendIconToken(profile.layout, profile.icon_image)
                : null,
          },
        ],
      };
    case 'categorical':
      return {
        id: key,
        source_id: profile.source_id,
        source_layer: profile.source_layer,
        title: profile.label || profile.source_layer,
        entries: profile.categories
          .filter(category => !category.hidden)
          .map(category => ({
            id: `${profile.id}:${category.value}`,
            label: category.label,
            color: resolveLegendColor(profile.representation, category.paint, category.color),
            representation: profile.representation ?? null,
            icon_token:
              profile.representation === 'symbol'
                ? readLegendIconToken(category.layout, category.icon_image)
                : null,
          })),
      };
    case 'range':
      return {
        id: key,
        source_id: profile.source_id,
        source_layer: profile.source_layer,
        title: profile.label || profile.source_layer,
        entries: profile.breaks
          .filter(item => !item.hidden)
          .map((item, index) => ({
            id: item.id || `${profile.id}:${index}`,
            label: item.label,
            color: resolveLegendColor(profile.representation, item.paint, item.color),
            representation: profile.representation ?? null,
            icon_token:
              profile.representation === 'symbol'
                ? readLegendIconToken(item.layout, item.icon_image)
                : null,
          })),
      };
    case 'rules':
      return null;
    default:
      return null;
  }
};

const readLayerTarget = (
  layer: Record<string, unknown>,
  styleSources: Record<string, any>,
): { key: TargetKey; keys: TargetKey[]; sourceId: string; sourceAlias: string; sourceLayer: string; kind: string; meta: WorkbenchMeta } | null => {
  const sourceAlias = typeof layer.source === 'string' ? layer.source : '';
  const sourceLayer = typeof layer['source-layer'] === 'string' ? (layer['source-layer'] as string) : '';
  const metadata = isRecord(layer.metadata) ? (layer.metadata as Record<string, unknown>) : {};
  const workbench = isRecord(metadata.workbench) ? ((metadata.workbench as WorkbenchMeta) ?? {}) : {};
  const sourceIdFromMeta = typeof workbench.source_id === 'string' ? workbench.source_id : '';
  const sourceIdFromUrl =
    typeof styleSources[sourceAlias]?.url === 'string' && styleSources[sourceAlias].url.startsWith('pmtiles://')
      ? styleSources[sourceAlias].url.slice('pmtiles://'.length)
      : '';
  const sourceIdFromData =
    typeof styleSources[sourceAlias]?.data === 'string' && styleSources[sourceAlias].data.startsWith('geojson://')
      ? styleSources[sourceAlias].data.slice('geojson://'.length)
      : typeof styleSources[sourceAlias]?.data === 'string'
        ? styleSources[sourceAlias].data.match(MAP_SOURCE_GEOJSON_PATTERN)?.[1] ?? ''
        : '';
  const sourceId = sourceIdFromMeta || sourceIdFromUrl || sourceIdFromData;
  const resolvedSourceLayer = (typeof workbench.source_layer === 'string' ? workbench.source_layer : sourceLayer) || '';
  const layerType = typeof layer.type === 'string' ? (layer.type as string) : '';
  const kind = typeof workbench.kind === 'string' ? workbench.kind : layerType;
  if (!sourceId || !resolvedSourceLayer || !kind) {
    return null;
  }
  return {
    key: buildTargetKey(sourceId, resolvedSourceLayer),
    keys: buildTargetKeys(sourceId, resolvedSourceLayer, sourceAlias),
    sourceId,
    sourceAlias,
    sourceLayer: resolvedSourceLayer,
    kind,
    meta: workbench,
  };
};

const buildCompiledWorkbenchMeta = (
  target: { sourceId: string; sourceLayer: string; meta: WorkbenchMeta },
): WorkbenchMeta => ({
  ...target.meta,
  managed: true,
  source_id: target.sourceId,
  source_layer: target.sourceLayer,
});

export type ResolvedMapExperienceRenderStackEntry = {
  entryId: string;
  enabled: boolean;
  label: string | null;
  stackRole: MapExperienceRenderStackRole;
  profile: MapExperienceStyleProfile;
  filterLogic: StyleRuleLogic | null;
  synthetic: boolean;
};

const resolveRenderStackEntries = (args: {
  experience: MapExperienceMetadataV1;
  target: { key: TargetKey; keys: TargetKey[]; sourceId: string; sourceAlias: string; sourceLayer: string };
  profilesById: Map<string, MapExperienceStyleProfile>;
  targetProfiles: MapExperienceStyleProfile[];
  activeProfile: MapExperienceStyleProfile | null;
}): ResolvedMapExperienceRenderStackEntry[] => {
  const storedStack =
    args.experience.render_stacks?.find(
      stack =>
        matchesExperienceSourceId(stack.source_id, args.target.sourceId, args.target.sourceAlias) &&
        stack.source_layer === args.target.sourceLayer,
    ) ?? null;
  const baseProfile = args.activeProfile ?? args.targetProfiles[0] ?? null;
  const resolvedStored = (storedStack?.entries ?? [])
    .map(entry => {
      const profile = args.profilesById.get(entry.profile_id) ?? null;
      if (!profile) {
        return null;
      }
      return {
        entryId: entry.entry_id,
        enabled: entry.enabled !== false,
        label: entry.label ?? profile.label ?? null,
        stackRole: entry.stack_role ?? 'overlay_symbol',
        profile,
        filterLogic: entry.filter_logic ?? null,
        synthetic: false,
      } satisfies ResolvedMapExperienceRenderStackEntry;
    })
    .filter((entry): entry is ResolvedMapExperienceRenderStackEntry => Boolean(entry));
  if (resolvedStored.length > 0) {
    const hasBaseEntry = resolvedStored.some(entry => entry.stackRole === 'base');
    if (hasBaseEntry || !baseProfile) {
      return resolvedStored;
    }
    return [{
      entryId: 'base',
      enabled: true,
      label: baseProfile.label ?? null,
      stackRole: 'base',
      profile: baseProfile,
      filterLogic: null,
      synthetic: true,
    }, ...resolvedStored];
  }
  if (!baseProfile) {
    return [];
  }
  return [{
    entryId: 'base',
    enabled: true,
    label: baseProfile.label ?? null,
    stackRole: 'base',
    profile: baseProfile,
    filterLogic: null,
    synthetic: true,
  }];
};

export const resolveMapExperienceRenderStackEntries = (args: {
  experience: MapExperienceMetadataV1;
  sourceId: string;
  sourceAlias?: string | null;
  sourceLayer: string;
}): ResolvedMapExperienceRenderStackEntry[] => {
  const experience = serializeMapExperienceMetadata(args.experience);
  const targetKey = buildTargetKey(args.sourceId, args.sourceLayer);
  const targetKeys = buildTargetKeys(args.sourceId, args.sourceLayer, args.sourceAlias);
  const profilesById = new Map(experience.style_profiles.map(profile => [profile.id, profile]));
  const targetProfiles = experience.style_profiles.filter(
    profile =>
      matchesExperienceSourceId(profile.source_id, args.sourceId, args.sourceAlias) &&
      profile.source_layer === args.sourceLayer,
  );
  const activeProfileId = targetKeys
    .map(key => experience.active_style_profiles[key] ?? '')
    .find(Boolean) ?? '';
  const explicitlyActiveProfile = profilesById.get(activeProfileId) ?? null;
  const activeProfile =
    explicitlyActiveProfile ??
    targetProfiles.find(profile => profile.kind === 'rules') ??
    targetProfiles.find(profile => profile.kind !== 'rules') ??
    null;

  return resolveRenderStackEntries({
    experience,
    target: {
      key: targetKey,
      keys: targetKeys,
      sourceId: args.sourceId,
      sourceAlias: args.sourceAlias?.trim() ?? '',
      sourceLayer: args.sourceLayer,
    },
    profilesById,
    targetProfiles,
    activeProfile,
  });
};

const buildLegacyRuleLayerFromProfile = (args: {
  profile: MapExperienceRulesStyleProfile;
  sourceAlias?: string | null;
  themeColors?: MapExperienceThemeColorPalette | null;
  colorMode?: MapExperienceCompileColorMode;
  globalStateDefaults?: Map<MapExperienceSemanticColorToken, string>;
}): LayerRuleLayerState => {
  const profileLayout = normalizeMapLibreLayoutOverrides(
    isRecord(args.profile.layout) ? (args.profile.layout as Record<string, unknown>) : null,
  );
  return {
    sourceId: args.profile.source_id,
    sourceAlias: args.sourceAlias?.trim() || args.profile.source_id,
    sourceLayer: args.profile.source_layer,
    mode: args.profile.mode ?? 'hide_non_matching',
    fields: [],
    rules: (args.profile.rules ?? []).map(rule => {
      const nextPaint = normalizeMapLibrePaintOverrides(isRecord(rule.paint) ? (rule.paint as Record<string, unknown>) : null);
      if (nextPaint) {
        Object.entries(nextPaint).forEach(([prop, value]) => {
          if (!STYLE_COLOR_PROP_NAMES.has(prop)) {
            return;
          }
          const normalizedColor = normalizeOptionalMapExperienceColorValue(value);
          if (!normalizedColor) {
            return;
          }
          nextPaint[prop] = resolveCompiledMapExperienceColorValue(
            normalizedColor,
            args.themeColors,
            args.colorMode ?? 'literal',
            args.globalStateDefaults ?? new Map(),
            DEFAULT_SINGLE_COLOR,
          );
        });
      }
      const nextLayout = normalizeMapLibreLayoutOverrides(
        isRecord(rule.layout) ? (rule.layout as Record<string, unknown>) : null,
      );
      return {
        ...rule,
        logic: ensureLogicRoot(rule.logic),
        representation: rule.representation ?? args.profile.representation ?? null,
        paint: nextPaint,
        layout: profileLayout || nextLayout ? { ...(profileLayout ?? {}), ...(nextLayout ?? {}) } : null,
      };
    }),
  };
};

export const migrateLegacyLayerRulesToMapExperience = (
  experienceInput: MapExperienceMetadataV1,
  ruleBuilder: LayerRuleBuilderState | null | undefined,
): MapExperienceMetadataV1 => {
  const experience = serializeMapExperienceMetadata(experienceInput);
  if (!ruleBuilder || ruleBuilder.layers.length === 0) {
    return experience;
  }

  const profiles = [...experience.style_profiles];
  const activeProfiles = { ...experience.active_style_profiles };
  let changed = false;

  ruleBuilder.layers.forEach(layer => {
    const targetKey = buildTargetKey(layer.sourceId, layer.sourceLayer);
    const rulesProfileId = `rules::${targetKey}`;
    const existingProfile = profiles.find(
      (profile): profile is MapExperienceRulesStyleProfile =>
        profile.id === rulesProfileId &&
        profile.source_id === layer.sourceId &&
        profile.source_layer === layer.sourceLayer &&
        profile.kind === 'rules',
    ) ?? null;
    const nextProfile: MapExperienceRulesStyleProfile = existingProfile
      ? {
          ...existingProfile,
          mode: 'hide_non_matching',
          rules: (layer.rules ?? []).map(rule => ({
            ...rule,
            logic: ensureLogicRoot(rule.logic),
          })),
        }
      : {
          id: rulesProfileId,
          source_id: layer.sourceId,
          source_layer: layer.sourceLayer,
          kind: 'rules',
          label: `${layer.sourceLayer} rules`,
          mode: 'hide_non_matching',
          rules: (layer.rules ?? []).map(rule => ({
            ...rule,
            logic: ensureLogicRoot(rule.logic),
          })),
        };
    if (existingProfile) {
      const existingSerialized = JSON.stringify(existingProfile);
      const nextSerialized = JSON.stringify(nextProfile);
      if (existingSerialized !== nextSerialized) {
        const index = profiles.findIndex(profile => profile.id === existingProfile.id);
        profiles[index] = nextProfile;
        changed = true;
      }
    } else {
      profiles.push(nextProfile);
      changed = true;
    }

    const activeProfileId = activeProfiles[targetKey] ?? '';
    const targetProfiles = experience.style_profiles.filter(
      profile => profile.source_id === layer.sourceId && profile.source_layer === layer.sourceLayer,
    );
    const onlyRulesProfiles = targetProfiles.length > 0 && targetProfiles.every(profile => profile.kind === 'rules');
    if ((!activeProfileId && onlyRulesProfiles) || activeProfileId === rulesProfileId) {
      activeProfiles[targetKey] = rulesProfileId;
    }
  });

  if (!changed) {
    return {
      ...experience,
      active_style_profiles: activeProfiles,
    };
  }

  return serializeMapExperienceMetadata({
    ...experience,
    style_profiles: profiles,
    active_style_profiles: activeProfiles,
  });
};

export const compileStyleWithMapExperience = (
  styleJson: Record<string, unknown>,
  experienceInput: MapExperienceMetadataV1,
  legacyRuleBuilder?: LayerRuleBuilderState | null,
  themeColors?: MapExperienceThemeColorPalette | null,
  colorMode: MapExperienceCompileColorMode = 'literal',
): {
  styleJson: Record<string, unknown>;
  legends: MapExperienceLegendGroup[];
} => {
  const experience = migrateLegacyLayerRulesToMapExperience(experienceInput, legacyRuleBuilder ?? null);
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, any>)
      : {};
  const nextStyle = cloneJsonValue(styleJson);
  const nextLayers = Array.isArray(nextStyle.layers) ? (nextStyle.layers as Array<Record<string, unknown>>) : [];
  const profilesById = new Map(experience.style_profiles.map(profile => [profile.id, profile]));
  const legends = new Map<string, MapExperienceLegendGroup>();
  const hiddenLayerFilter: unknown[] = ['all', ['in', ['id'], ['literal', []]]];
  const globalStateDefaults = new Map<MapExperienceSemanticColorToken, string>();

  nextStyle.layers = nextLayers.flatMap(layer => {
    const target = readLayerTarget(layer, styleSources);
    if (!target) return layer;

    const targetProfiles = experience.style_profiles.filter(
      profile =>
        matchesExperienceSourceId(profile.source_id, target.sourceId, target.sourceAlias) &&
        profile.source_layer === target.sourceLayer,
    );
    const activeProfileId =
      target.keys
        .map(key => experience.active_style_profiles[key] ?? '')
        .find(Boolean) ?? '';
    const explicitlyActiveProfile = profilesById.get(activeProfileId) ?? null;
    const activeProfile =
      explicitlyActiveProfile ??
      targetProfiles.find(profile => profile.kind === 'rules') ??
      targetProfiles.find(profile => profile.kind !== 'rules') ??
      null;
    const currentLayerType = typeof layer.type === 'string' ? (layer.type as string) : '';
    const targetFilterExpr = buildTargetFilterExpression(target.keys, experience);

    const renderEntries = resolveRenderStackEntries({
      experience,
      target,
      profilesById,
      targetProfiles,
      activeProfile,
    });
    const targetHasBadgeOverlay = renderEntries.some(entry => entry.enabled && entry.stackRole === 'overlay_badge');
    if (renderEntries.length === 0) {
      const nextLayer = { ...layer };
      const nextMetadata = isRecord(nextLayer.metadata) ? ({ ...(nextLayer.metadata as Record<string, unknown>) }) : {};
      const workbench = buildCompiledWorkbenchMeta(target);
      nextMetadata.workbench = workbench as unknown as Record<string, unknown>;
      nextLayer.metadata = nextMetadata;
      const baseline = ensureExperienceBaseline(nextLayer, currentLayerType || target.kind, workbench);
      applyCombinedFilter(nextLayer, baseline, targetFilterExpr);
      return [nextLayer];
    }

    return renderEntries
      .filter(entry => entry.enabled)
      .map((entry, index) => {
        const nextLayer = { ...layer };
        const nextMetadata = isRecord(nextLayer.metadata) ? ({ ...(nextLayer.metadata as Record<string, unknown>) }) : {};
        const workbench = buildCompiledWorkbenchMeta(target);
        workbench.render_stack_entry_id = entry.entryId;
        workbench.render_stack_role = entry.stackRole;
        const interactionHitTargetPx =
          typeof entry.profile.interaction?.hit_target_px === 'number' && Number.isFinite(entry.profile.interaction.hit_target_px)
            ? Math.max(0, entry.profile.interaction.hit_target_px)
            : null;
        if (interactionHitTargetPx != null) {
          workbench.interaction_hit_target_px = interactionHitTargetPx;
        } else {
          delete workbench.interaction_hit_target_px;
        }
        nextMetadata.workbench = workbench as unknown as Record<string, unknown>;
        nextLayer.metadata = nextMetadata;

        if (!entry.synthetic) {
          nextLayer.id = `${String(layer.id ?? `${target.key}-layer`)}--stack-${index + 1}-${slugifyRenderStackIdPart(entry.entryId)}`;
        }

        const compiledKind = resolveCompiledLayerKind(target.kind, currentLayerType, entry.profile);
        nextLayer.type = compiledKind;
        const baseline = ensureExperienceBaseline(nextLayer, compiledKind, workbench);
        const paint = isRecord(nextLayer.paint) ? ({ ...(nextLayer.paint as Record<string, unknown>) }) : {};
        const layout = isRecord(nextLayer.layout) ? ({ ...(nextLayer.layout as Record<string, unknown>) }) : {};
        normalizeCompiledLayerRepresentationState(paint, layout, compiledKind);
        applyProfilePaint(paint, baseline, compiledKind, entry.profile, themeColors, colorMode, globalStateDefaults);
        applyProfileLayout(layout, compiledKind, entry.profile);
        applyRenderStackSymbolOverlayDefaults(layout, entry, compiledKind, { targetHasBadgeOverlay });
        applyCompiledLayerKindDefaults(paint, compiledKind);
        nextLayer.paint = paint;
        nextLayer.layout = layout;

        const renderEntryFilter = buildRenderStackFilterExpression(entry.filterLogic);
        const combinedFilter = combineFilterExpressions(targetFilterExpr, renderEntryFilter);
        applyCombinedFilter(nextLayer, baseline, combinedFilter);

        const rulesProfileState =
          entry.profile.kind === 'rules'
            ? buildLegacyRuleLayerFromProfile({
                profile: entry.profile,
                sourceAlias: typeof target.meta.source_alias === 'string' ? target.meta.source_alias : null,
                themeColors,
                colorMode,
                globalStateDefaults,
              })
            : null;
        const compiledLayer =
          rulesProfileState
            ? (
                compileStyleWithLayerRules(
                  {
                    version: styleJson.version ?? 8,
                    sources: styleSources,
                    layers: [nextLayer],
                  },
                  {
                    version: 1,
                    layers: [rulesProfileState],
                  },
                ).layers as Array<Record<string, unknown>>
              )[0] ?? nextLayer
            : nextLayer;

        const legendGroup =
          rulesProfileState
            ? buildRulesLegendGroup(`${target.key}::${entry.entryId}`, target.sourceId, target.sourceLayer, rulesProfileState, themeColors)
            : buildProfileLegendGroup(`${target.key}::${entry.entryId}`, entry.profile, themeColors);
        if (legendGroup && legendGroup.entries.length > 0) {
          legends.set(`${target.key}::${entry.entryId}`, {
            ...legendGroup,
            title:
              entry.profile.kind === 'rules'
                ? entry.profile.label || entry.label || legendGroup.title
                : entry.label || legendGroup.title,
          });
        }

        return compiledLayer;
      });
  });

  return {
    styleJson: mergeCompiledMapExperienceStateDefaults(nextStyle, globalStateDefaults),
    legends: Array.from(legends.values()),
  };
};

export const buildMapExperiencePreset = (args: {
  id: string;
  label: string;
  viewport?: MapExperienceViewState | null;
  layerVisibility?: Record<string, boolean> | null;
  activeStyleProfiles: Record<string, string>;
  activeFilterValues: Record<string, Record<string, unknown>>;
}): MapExperiencePresetDefinition => ({
  id: args.id,
  label: args.label,
  viewport: args.viewport ?? null,
  layer_visibility: args.layerVisibility
    ? Object.fromEntries(
        Object.entries(args.layerVisibility).filter(([, entry]) => typeof entry === 'boolean')
      )
    : null,
  active_style_profiles: normalizeStringRecord(args.activeStyleProfiles),
  active_filter_values: normalizeActiveFilterValues(args.activeFilterValues),
});

export const applyMapExperiencePreset = (
  experienceInput: MapExperienceMetadataV1,
  presetId: string | null | undefined,
): {
  experience: MapExperienceMetadataV1;
  viewport: MapExperienceViewState | null;
  layerVisibility: Record<string, boolean> | null;
} => {
  const experience = serializeMapExperienceMetadata(experienceInput);
  const preset = experience.presets.find(entry => entry.id === presetId) ?? null;
  if (!preset) {
    return { experience, viewport: null, layerVisibility: null };
  }
  return {
    viewport: preset.viewport ?? null,
    layerVisibility: preset.layer_visibility ?? null,
    experience: {
      ...experience,
      selected_preset_id: preset.id,
      active_style_profiles: normalizeStringRecord(preset.active_style_profiles),
      active_filter_values: normalizeActiveFilterValues(preset.active_filter_values),
    },
  };
};

export const upsertMapExperiencePreset = (
  experienceInput: MapExperienceMetadataV1,
  preset: MapExperiencePresetDefinition,
): MapExperienceMetadataV1 => {
  const experience = serializeMapExperienceMetadata(experienceInput);
  const nextPreset = normalizePreset(preset);
  if (!nextPreset) return experience;
  const nextPresets = experience.presets.filter(entry => entry.id !== nextPreset.id);
  nextPresets.push(nextPreset);
  return {
    ...experience,
    presets: nextPresets,
    selected_preset_id: nextPreset.id,
  };
};

export const removeMapExperiencePreset = (
  experienceInput: MapExperienceMetadataV1,
  presetId: string,
): MapExperienceMetadataV1 => {
  const experience = serializeMapExperienceMetadata(experienceInput);
  return {
    ...experience,
    presets: experience.presets.filter(entry => entry.id !== presetId),
    selected_preset_id: experience.selected_preset_id === presetId ? null : experience.selected_preset_id,
  };
};

export const buildMapExperienceTargetKey = buildTargetKey;
